/**
 * API client for the Asthra Word Widget backend.
 */

const API_BASE = import.meta.env.VITE_API_BASE || "/api";

const LEGACY_SESSION_TOKEN_KEY = "asthra_session_token";
const SESSION_USERNAME_KEY = "asthra_session_username";
const SESSION_TOKEN_KEY = "asthra_access_token";

let sessionUsername: string | null = null;
let usernameHydrated = false;
let sessionToken: string | null = null;
let tokenHydrated = false;

function readSessionStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorage(key: string, value: string | null): void {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors in restricted browser contexts.
  }
}

function clearLegacySessionToken(): void {
  writeSessionStorage(LEGACY_SESSION_TOKEN_KEY, null);
}

export function setSessionUsername(username: string | null) {
  sessionUsername = username;
  usernameHydrated = true;
  writeSessionStorage(SESSION_USERNAME_KEY, username);
}

export function getSessionUsername(): string | null {
  if (!usernameHydrated) {
    sessionUsername = readSessionStorage(SESSION_USERNAME_KEY);
    usernameHydrated = true;
  }
  return sessionUsername;
}

function setSessionToken(token: string | null) {
  sessionToken = token;
  tokenHydrated = true;
  writeSessionStorage(SESSION_TOKEN_KEY, token);
}

export function getSessionToken(): string | null {
  if (!tokenHydrated) {
    sessionToken = readSessionStorage(SESSION_TOKEN_KEY);
    tokenHydrated = true;
  }
  return sessionToken;
}

/**
 * The config the shared `@shared/client` wire modules take — a
 * `ClientConfig = { apiBase, token }` — so the add-in and the web app send the
 * SAME request for a shared feature (folders, submissions). Keep both surfaces
 * on this seam rather than duplicating fetch logic per surface.
 */
export function sharedClientConfig(): { apiBase: string; token: string } {
  return { apiBase: API_BASE, token: getSessionToken() ?? "" };
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = getSessionToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    // Detail can be either a string or a structured object (e.g. our
    // pause_pending 409 response: { code, message, retry_after_seconds }).
    const detailValue = (detail as { detail?: unknown }).detail;
    let message: string;
    if (typeof detailValue === "string") {
      message = detailValue;
    } else if (
      detailValue &&
      typeof detailValue === "object" &&
      "message" in (detailValue as Record<string, unknown>)
    ) {
      message = String((detailValue as { message: unknown }).message);
    } else {
      message = `HTTP ${resp.status}`;
    }
    const err = new Error(message) as Error & {
      status?: number;
      detail?: unknown;
    };
    err.status = resp.status;
    err.detail = detailValue;
    throw err;
  }

  return resp.json();
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function login(username: string, password: string): Promise<{ username: string }> {
  const data = await request<{ username: string; access_token?: string }>("POST", "/auth/login", {
    username,
    password,
  });
  clearLegacySessionToken();
  setSessionUsername(data.username);
  if (data.access_token) {
    setSessionToken(data.access_token);
  }
  return data;
}

/**
 * Poll the browser-hand-off endpoint once, storing the session if it's ready.
 *
 * The add-in's side of the browser sign-in: after the user signs in on the web
 * app (in their system browser), the web app parks the session under the nonce
 * the add-in generated. This collects it. Returns `null` while nothing is parked
 * yet (HTTP 204 — keep polling); on success it stores username + token exactly
 * like `login` and returns the username.
 *
 * Deliberately not routed through `request`: a 204 has no JSON body, so this
 * reads the status directly rather than trying to parse an empty response. No
 * Authorization header — the endpoint is public and the nonce is the credential.
 */
export async function redeemAuthHandoff(nonce: string): Promise<{ username: string } | null> {
  const resp = await fetch(`${API_BASE}/auth/handoff/${nonce}`, {
    method: "GET",
    credentials: "include",
  });

  if (resp.status === 204) return null; // nothing parked yet
  if (!resp.ok) {
    const err = new Error(`HTTP ${resp.status}`) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }

  const data = (await resp.json()) as { username: string; access_token?: string };
  clearLegacySessionToken();
  setSessionUsername(data.username);
  if (data.access_token) {
    setSessionToken(data.access_token);
  }
  return { username: data.username };
}

export async function logout(): Promise<void> {
  // The authoritative client-side logout is clearing the local session; the
  // POST is a best-effort server-side session invalidation. Clear the local
  // session in a ``finally`` so an expired/invalid token (the POST 401s) still
  // logs the user out — otherwise the stale token survives the reload and the
  // app rehydrates it, landing back on the homepage with "Token expired".
  try {
    await request("POST", "/auth/logout");
  } finally {
    clearLegacySessionToken();
    setSessionUsername(null);
    setSessionToken(null);
  }
}

/** Set a new password on the signed-in account. The session stays valid. */
export async function changePassword(newPassword: string, confirmPassword: string): Promise<void> {
  await request("POST", "/account/password", {
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

import type { CheckpointInfo, DocumentInfo, GateInfo, Library, ModuleInfo, Workspace } from "../types";
import { relativePathOf } from "../variant-c/library-hub/folderPaths";

export async function getWorkspaces(): Promise<Workspace[]> {
  return request("GET", "/workspaces");
}

export async function getLibraries(workspaceId: string): Promise<Library[]> {
  return request("GET", `/workspaces/${workspaceId}/libraries`);
}

/** Documents per page of ``GET /libraries/{id}/documents`` — the route's ceiling. */
const DOCUMENTS_PAGE = 1000;
/** Stop after this many pages (50,000 documents) whatever the server says. */
const DOCUMENTS_MAX_PAGES = 50;

/** Every document in a library. The route pages (100 by default, 1,000 at
 *  most), so a library past the first page — a bulk upload of thousands —
 *  is read page by page until a short page comes back. */
export async function getDocuments(libraryId: string): Promise<DocumentInfo[]> {
  const byId = new Map<string, DocumentInfo>();
  for (let page = 0; page < DOCUMENTS_MAX_PAGES; page++) {
    const docs = await request<DocumentInfo[]>(
      "GET",
      `/libraries/${libraryId}/documents?skip=${page * DOCUMENTS_PAGE}&limit=${DOCUMENTS_PAGE}`,
    );
    for (const doc of docs) byId.set(doc.id, doc);
    if (docs.length < DOCUMENTS_PAGE) break;
  }
  return [...byId.values()];
}

export async function createLibrary(workspaceId: string, name: string): Promise<{ status: string; library: Library }> {
  // Body mirrors Sangnan's ``add_library`` contract: name + desc +
  // public + default_contribute. ``target_doctype``/``scope`` are NOT
  // sent — the server has no such field yet, so the writer's scope
  // pick is persisted client-side via ``libraryDoctypeStore``.
  return request("POST", `/workspaces/${workspaceId}/libraries`, {
    name,
    desc: "",
    public: true,
    default_contribute: true,
  });
}

/**
 * Create a custom (billable) workspace.
 *
 * A custom workspace is the container a paid draft's custom library lives in.
 * The demo workspace and its sample libraries stay free (billing-exempt); a
 * workspace a user creates for themselves is billable by default. Private
 * unless asked otherwise; the upstream plan is fixed server-side.
 */
export async function createWorkspace(name: string, opts?: { isPublic?: boolean }): Promise<Workspace> {
  return request("POST", "/workspaces", {
    name,
    is_public: opts?.isPublic ?? false,
  });
}

// ---------------------------------------------------------------------------
// Library assets (chat @-mention picker: section titles / table captions)
// ---------------------------------------------------------------------------

import type { MentionAttachment } from "../types";

/** One section heading or table caption in a library document. */
export interface LibraryAssetItem {
  /** The label shown to the writer — later the find_section/find_table key. */
  title: string;
  file_id: string;
  file_name: string;
}

export interface LibraryAssetListResult {
  library_id: string;
  asset_type: "section" | "table";
  assets: LibraryAssetItem[];
  /** Distinct source-file set in the result, for the picker's file stage. */
  files: { file_id: string; file_name: string }[];
}

/** List the library's sections or tables, optionally narrowed to one file / keyword. */
export async function listLibraryAssets(
  libraryId: string,
  assetType: "section" | "table",
  opts: { fileId?: string | null; q?: string | null } = {},
): Promise<LibraryAssetListResult> {
  const params = new URLSearchParams({ asset_type: assetType });
  if (opts.fileId) params.set("file_id", opts.fileId);
  if (opts.q) params.set("q", opts.q);
  return request("GET", `/libraries/${encodeURIComponent(libraryId)}/assets?${params.toString()}`);
}

/** Map an app-side attachment to the server wire shape (chat_attachments.ChatAttachment). */
export function toWireAttachment(a: MentionAttachment): Record<string, string> {
  return { asset_type: a.assetType, file_id: a.fileId, file_name: a.fileName, label: a.label };
}

export async function deleteDocument(libraryId: string, documentId: string): Promise<void> {
  await request("DELETE", `/libraries/${libraryId}/documents/${documentId}`);
}

// ---------------------------------------------------------------------------
// Table artefacts — tag source tables, transform them (merge/filter/computed
// columns), and PREVIEW a derived CSV. This call never saves; the caller
// persists an approved preview via uploadContent(..., "text/csv").
// ---------------------------------------------------------------------------

export interface TableArtefactPreview {
  library_id: string;
  columns: string[];
  row_count: number;
  preview_rows: string[][];
  truncated_preview: boolean;
  csv: string;
  markdown: string;
  suggested_filename: string;
  provenance: { source_transaction_ids: string[]; spec: unknown };
  /** How the table was built: the step trace and inputs (create_artefact). */
  derivation?: { trace?: { kind: string; text: string }[]; inputs?: { handle?: string; source?: string }[] };
  /** The table is saved to the library when built; Approve keeps it, Discard deletes it. */
  saved_file?: { filename: string; library_id: string; status: "saving" | "saved" | "failed"; asthra_doc_id?: string; error?: string } | null;
}

/** Build a preview of the derived table (blocking). Server-side this is read-only. */
export async function previewTableArtefact(
  libraryId: string,
  tables: MentionAttachment[],
  instruction: string,
  outputName = "",
): Promise<TableArtefactPreview> {
  return request("POST", `/libraries/${encodeURIComponent(libraryId)}/table-artefacts`, {
    tables: tables.map(toWireAttachment),
    instruction,
    output_name: outputName,
  });
}

/** One progress event while an artefact is being built. */
export interface ArtefactStageEvent {
  type: "stage";
  /** resolving | parsed | planning | executing | step */
  step: string;
  message: string;
  index?: number;
  total?: number;
}

type ArtefactStreamEvent =
  | ArtefactStageEvent
  | { type: "result"; result: TableArtefactPreview }
  | { type: "error"; error: string };

/**
 * Build a derived table over SSE, calling ``onStage`` for each progress event
 * and resolving with the preview once the ``result`` event arrives. Uses
 * ``fetch`` + manual SSE parsing (EventSource can't send the auth header),
 * matching ``streamLibraryInventory``.
 */
export async function streamTableArtefact(
  libraryId: string,
  tables: MentionAttachment[],
  instruction: string,
  onStage: (event: ArtefactStageEvent) => void,
  signal?: AbortSignal,
): Promise<TableArtefactPreview> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const resp = await fetch(`${API_BASE}/libraries/${encodeURIComponent(libraryId)}/table-artefacts/stream`, {
    method: "POST",
    credentials: "include",
    headers,
    signal,
    body: JSON.stringify({
      tables: tables.map(toWireAttachment),
      instruction,
      output_name: "",
    }),
  });
  if (!resp.ok || !resp.body) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(detail.detail || `HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: TableArtefactPreview | null = null;
  let errorMessage: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data: ")) continue;
      try {
        const event = JSON.parse(line.slice(6)) as ArtefactStreamEvent;
        if (event.type === "stage") onStage(event);
        else if (event.type === "result") result = event.result;
        else if (event.type === "error") errorMessage = event.error || "unknown error";
      } catch {
        // Skip malformed events / keepalives.
      }
    }
  }

  if (errorMessage) throw new Error(errorMessage);
  if (!result) throw new Error("artefact stream closed before emitting a result");
  return result;
}

// ---------------------------------------------------------------------------
// Library resolution (Spec 12)
//
// These three calls back the writer-approval flow. Inventory is the
// resolver's classification + multiplicity output for a library;
// the writer reviews and signs off via approve(); the audit trail
// is read via getApprovals().
// ---------------------------------------------------------------------------

export interface ResolvedClassification {
  doc_id: string;
  doc_name: string;
  doc_type: string;
  confidence: string;
  resolved_by: string;
  evidence: string;
  classified_at: string;
  requires_writer_input: boolean;
  // Spec 34 D-2 — additive, optional. Backend (M1a) populates from
  // upstream Sangnan ``document.{status, parsed}``; older server
  // builds simply don't emit these and the frontend reads undefined.
  processing_status?: string | null;
  parsed?: boolean | null;
  // Sign-off (added in this MR — Spec 34 §"data-model" reconciles
  // with the existing LibraryApproval mechanism). Optional so older
  // cache records reload without these.
  verified?: boolean;
  verified_by?: string;
  verified_at?: string;
  // Spec 34 label params — optional metadata surfaced in the Hub UI.
  label_params?: Record<string, string | null> | null;
}

export interface ResolvedCluster {
  doc_type: string;
  stem: string;
  primary_doc_id: string;
  member_doc_ids: string[];
  roles: Record<string, string>;
  notes: Record<string, string>;
}

/**
 * Spec 32 source-doctype classification. Per-doc structured tags
 * the drafting agent uses to disambiguate within-type retrievals
 * (which BMR? which CofA?). Populated by the production harness
 * during inventory rebuild — see Spec 32 §"M4".
 */
export interface ResolvedSourceDocClassification {
  doc_id: string;
  doc_name: string;
  source_doctype: string;
  tags: Record<string, string | null>;
  confidence: Record<string, number>;
  rationale: string;
  classified_at: string;
  classifier_version: string;
  error: string | null;
}

export interface ResolvedInventoryDTO {
  library_id: string;
  library_snapshot_hash: string;
  state: string;
  classifications: ResolvedClassification[];
  clusters: ResolvedCluster[];
  current_approval_id: string | null;
  resolved_at: string;
  // Spec 32 — additive, optional. Empty when the doctype YAML has
  // no ``source_subtype_tags`` block (doctype hasn't onboarded).
  source_classifications?: ResolvedSourceDocClassification[];
}

export interface LibraryApprovalDTO {
  approval_id: string;
  library_id: string;
  library_snapshot_hash: string;
  scope: string;
  approved_at: string;
  approved_by: string;
  approval_agent: string;
  classifications: Array<Record<string, unknown>>;
  multiplicity_decisions: Array<Record<string, unknown>>;
  supersedes_approval_id: string | null;
}

/** Query string for the inventory routes. ``classify: false`` asks for the
 *  label-free read (no LLM labelling, nothing persisted): the document list,
 *  live processing status and any labels already saved. Drafting only needs
 *  that — labelling is an explicit Library Hub action. */
function inventoryQuery(targetDoctype: string | null | undefined, classify: boolean): string {
  const params = new URLSearchParams();
  if (targetDoctype) params.set("target_doctype", targetDoctype);
  if (!classify) params.set("classify", "false");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function getLibraryInventory(
  libraryId: string,
  targetDoctype?: string | null,
  opts: { classify?: boolean } = {},
): Promise<ResolvedInventoryDTO> {
  return request("GET", `/library/${libraryId}/inventory${inventoryQuery(targetDoctype, opts.classify ?? true)}`);
}

/**
 * SSE progress events emitted by ``GET /library/<id>/inventory/stream``.
 * Mirrors the ``on_event`` callback shape on the server (service.py).
 * The shape is a discriminated union on ``type``; consumers narrow on
 * the type to access event-specific fields.
 */
export type InventoryResolveEvent =
  | { type: "listing_documents"; message?: string; fetched?: number; total?: number | null }
  | { type: "checking_cache"; message?: string; doc_count?: number }
  | { type: "cache_hit"; message?: string; state?: string; doc_count?: number }
  | { type: "cache_miss"; message?: string }
  | {
      type: "classifying";
      message?: string;
      doc_name?: string;
      index?: number;
      total?: number;
    }
  | {
      type: "labelled";
      message?: string;
      doc_name?: string;
      label?: string;
      resolved_by?: string;
    }
  | { type: "params_extracting"; message?: string; doc_name?: string }
  | { type: "params_extracted"; message?: string; doc_name?: string }
  | { type: "done"; inventory: ResolvedInventoryDTO }
  | { type: "error"; error?: string };

/**
 * Stream library inventory resolution progress over SSE.
 *
 * Returns the final ``ResolvedInventoryDTO`` once the ``done`` event
 * arrives. ``onEvent`` fires for every progress event (checking_cache,
 * classifying, labelled, …) so the caller can drive a live UI.
 *
 * Uses ``fetch`` with manual SSE parsing rather than ``EventSource``
 * because the browser ``EventSource`` API doesn't let us send the
 * ``Authorization: Bearer …`` header. Matches the existing
 * ``streamChat`` pattern earlier in this file.
 */
export async function streamLibraryInventory(
  libraryId: string,
  targetDoctype: string | null | undefined,
  onEvent: (event: InventoryResolveEvent) => void,
  signal?: AbortSignal,
  opts: { classify?: boolean } = {},
): Promise<ResolvedInventoryDTO> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const qs = inventoryQuery(targetDoctype, opts.classify ?? true);
  const resp = await fetch(`${API_BASE}/library/${libraryId}/inventory/stream${qs}`, {
    method: "GET",
    credentials: "include",
    headers,
    signal,
  });
  if (!resp.ok || !resp.body) {
    // Extract detailed error message from 402 quota errors
    if (resp.status === 402) {
      try {
        const errorData = await resp.json();
        if (errorData?.detail?.message) {
          throw new Error(errorData.detail.message);
        }
      } catch (parseErr) {
        // If JSON parsing fails, fall through to generic error
      }
    }
    throw new Error(`HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let inventory: ResolvedInventoryDTO | null = null;
  let errorMessage: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data: ")) continue;
      try {
        const event = JSON.parse(line.slice(6)) as InventoryResolveEvent;
        onEvent(event);
        if (event.type === "done") {
          inventory = event.inventory;
        } else if (event.type === "error") {
          errorMessage = event.error || "unknown error";
        }
      } catch {
        // Skip malformed events / keepalives.
      }
    }
  }

  if (errorMessage) throw new Error(errorMessage);
  if (!inventory) {
    throw new Error("inventory stream closed before emitting `done`");
  }
  return inventory;
}

export async function approveLibraryInventory(
  libraryId: string,
  body: {
    classifications: Array<{
      doc_id: string;
      doc_name?: string;
      doc_type: string;
      confidence?: string;
      evidence?: string;
    }>;
    multiplicity_decisions?: Array<{
      doc_type: string;
      stem?: string;
      primary_doc_id: string;
      member_doc_ids?: string[];
      roles?: Record<string, string>;
    }>;
    approval_agent?: string;
  },
): Promise<LibraryApprovalDTO> {
  return request("POST", `/library/${libraryId}/approve`, body);
}

export async function getLibraryApprovals(libraryId: string): Promise<LibraryApprovalDTO[]> {
  return request("GET", `/library/${libraryId}/approvals`);
}

export async function reclassifyDocument(
  libraryId: string,
  docId: string,
  body: { new_doc_type: string; edit_note?: string; approval_agent?: string },
): Promise<LibraryApprovalDTO> {
  return request("POST", `/library/${libraryId}/documents/${docId}/reclassify`, body);
}

/** Names a file inside an Upload action (for a server upload record). */
export interface UploadBatchRef {
  batchId: string;
  fileKey: string;
}

/** An upload that failed. ``recorded`` is true when the server already wrote
 *  the outcome to the batch record (Sangnan answered, or could not be reached);
 *  otherwise only this client knows the file failed. */
export class UploadError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly recorded: boolean,
  ) {
    super(message);
    this.name = "UploadError";
  }
}

/** The stored document's id in an upload response — same keys as the server's ``_uploaded_doc_id``. */
export function uploadedDocId(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const outer = body as Record<string, unknown>;
  // The library row's own id first: the nested ``document`` is the stored
  // file, and its ``id`` is not the row id the library lists and deletes by.
  const inner = (outer.document && typeof outer.document === "object" ? outer.document : {}) as Record<string, unknown>;
  const id = outer.id ?? outer.document_id ?? outer._id ?? inner.id ?? inner.document_id ?? inner._id;
  return id ? String(id) : null;
}

export async function uploadFile(
  libraryId: string,
  file: File,
): Promise<{ status: string; filename: string; doc_id: string | null }> {
  const formData = new FormData();
  formData.append("file", file);
  // A file from an uploaded folder keeps its path as the document's folder tag.
  const relativePath = relativePathOf(file);
  if (relativePath.includes("/")) formData.append("relative_path", relativePath);

  const uploadHeaders: Record<string, string> = {};
  const uploadToken = getSessionToken();
  if (uploadToken) {
    uploadHeaders.Authorization = `Bearer ${uploadToken}`;
  }

  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}/libraries/${libraryId}/upload`, {
      method: "POST",
      credentials: "include",
      headers: uploadHeaders,
      body: formData,
    });
  } catch {
    throw new UploadError("Couldn't reach the server.", null, false);
  }

  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    const message =
      typeof detail.detail === "string" && detail.detail ? detail.detail : `Upload failed: HTTP ${resp.status}`;
    // The upload route writes the outcome itself once it has asked Sangnan:
    // a refusal carries this prefix, and an unreachable Sangnan is a 5xx.
    // A 4xx of our own (sign-in, size) stopped before that.
    const recorded = message.startsWith("Library upload failed") || resp.status >= 500;
    throw new UploadError(message, resp.status, recorded);
  }

  const result = await resp.json();
  return { ...result, doc_id: uploadedDocId(result?.response) };
}

/** What ``POST /libraries/{id}/documents/{doc}/replace`` answers. */
export interface ReplacedDocument {
  name: string;
  old_doc_id: string;
  new_doc_id: string;
}

/**
 * Upload an updated version of a library document under its exact name. The
 * server waits for the new file to be processed before deleting the old one,
 * and keeps the old one if processing fails — so this can take minutes.
 */
export async function replaceDocument(libraryId: string, documentId: string, file: File): Promise<ReplacedDocument> {
  const formData = new FormData();
  formData.append("file", file);
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let resp: Response;
  try {
    resp = await fetch(
      `${API_BASE}/libraries/${encodeURIComponent(libraryId)}/documents/${encodeURIComponent(documentId)}/replace`,
      { method: "POST", credentials: "include", headers, body: formData },
    );
  } catch {
    throw new Error("Couldn't reach the server.");
  }
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(
      typeof detail.detail === "string" && detail.detail ? detail.detail : `Replace failed: HTTP ${resp.status}`,
    );
  }
  return resp.json();
}

/** Ask Sangnan to read a file it could not parse, again (the file is already stored). */
export async function reprocessDocument(libraryId: string, documentId: string): Promise<DocumentInfo> {
  return request(
    "POST",
    `/libraries/${encodeURIComponent(libraryId)}/documents/${encodeURIComponent(documentId)}/process`,
  );
}

// ---------------------------------------------------------------------------
// Library upload status — shapes only
// ---------------------------------------------------------------------------
//
// The Library Hub's upload tabs are typed against an upload record. This
// build has no server record yet (UI only), so these types describe the
// pane's own view; the record's routes come in a separate backend change.

/** Every state a file can be in. ``waiting`` is the only one that is not an outcome. */
export type UploadFileState = "waiting" | "uploaded" | "unconfirmed" | "failed" | "rejected" | "duplicate" | "skipped";

/** The states the client may report; the rest are the upload route's to write. */
export type ClientUploadState = "waiting" | "failed" | "rejected" | "duplicate" | "skipped";

export interface UploadManifestFile {
  key: string;
  name: string;
  relative_path?: string;
  size_bytes?: number | null;
  state?: ClientUploadState;
  message?: string | null;
}

/** A file of a batch that did not go in, or has not yet. */
export interface UploadProblem {
  key: string;
  name: string;
  folder: string;
  state: Exclude<UploadFileState, "uploaded" | "skipped">;
  message: string | null;
}

export interface UploadBatch {
  batch_id: string;
  /** Who started it, by sign-in name. */
  actor: string;
  started_at: string | null;
  updated_at: string | null;
  total_files: number;
  counts: Record<UploadFileState, number>;
  /** Files are still waiting and nothing has moved for an hour: whatever was sending them stopped. */
  stopped: boolean;
  problems: UploadProblem[];
  /** Sangnan's ids for the files that went in, to read their parsing from the document list. */
  uploaded_document_ids: string[];
}

export interface UploadStatus {
  library_id: string;
  batches: UploadBatch[];
}

// ---------------------------------------------------------------------------
// Content Upload
// ---------------------------------------------------------------------------

export async function uploadContent(
  libraryId: string,
  filename: string,
  content: string,
  contentType = "text/markdown",
): Promise<{ status: string; filename: string; library_id: string }> {
  return request("POST", `/libraries/${libraryId}/upload-content`, {
    filename,
    content,
    content_type: contentType,
  });
}

// ---------------------------------------------------------------------------
// Gates & Modules
// ---------------------------------------------------------------------------

export async function getGates(): Promise<GateInfo[]> {
  return request("GET", "/gates");
}

export async function getModules(): Promise<ModuleInfo[]> {
  return request("GET", "/modules");
}

/** The draft-content flag registry (flags.yaml): prefixes like
 *  ``[MISSING INFO:`` and the style each one renders with in Word. Lives at
 *  ``/draft-flags`` — ``/flags`` is the authenticated feature-flags route. */
export async function getDraftFlags(): Promise<{ flags?: unknown[] }> {
  return request("GET", "/draft-flags");
}

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

export interface StartRunParams {
  project_name: string;
  trial_name?: string; // backward compat alias — backend accepts both
  /** The writer-chosen draft name — required by the add-in for every new run
   *  (the shell asks for it before starting) and shown on the Drafts/Home
   *  lists. Continuations keep their run's name. */
  name?: string | null;
  doc_type?: string; // CER, CSR, PSUR, etc.
  doc_id?: string;
  workspace_id?: string;
  library_ids: string[];
  library_names?: Record<string, string>; // library_id → display name
  /** Where the document is submitted — a key of the doctype's `regions` (GET /doc-types). */
  region?: string;
  /** The writer's final comments from the section picker — appended to every
   *  section's instructions for this run. Omit when the writer left none. */
  run_instructions?: string;
  all_libraries?: boolean;
  module_ids?: string[];
  gates?: string[];
  review?: boolean;
  checkpoint_path?: string;
  output_dir?: string;
  connection_id?: string;
  plan_id?: string;
  insertion_mode_override?: "batch" | "section";
  // Approved library inventories, keyed by library_id. Same DTO shape as
  // GET /api/library/{id}/inventory. The UI sends the inventory it just
  // rendered/approved so the planner sees the writer-confirmed labels +
  // label_params without re-reading the cache on disk. Omit (or send {})
  // when no inventory is available — backend tolerates and warns.
  library_inventories?: Record<string, ResolvedInventoryDTO>;
  /** "custom" marks a run that builds on an EXISTING Word document the writer
   *  already had (headings read into a custom spine) rather than drafting a
   *  fresh document from the doctype template. Absent → normal fresh draft.
   *  A custom run is registered without dispatching the drafting worker. */
  submission_mode?: "custom";
  /** The document's heading hierarchy, read from the live Word doc, used to
   *  build the custom spine. Only meaningful when submission_mode === "custom". */
  custom_outline?: CustomSpineSection[];
}

/** One heading of an existing Word doc, as read into the custom spine. */
export interface CustomSpineSection {
  /** Synthetic module id (e.g. "CUSTOM-1") — the stable identity used by
   *  regenerate / audit routing for this custom section. */
  id: string;
  /** Display gutter number if the heading carried one; blank otherwise. */
  number: string;
  /** The heading text as it appears in the Word doc. */
  title: string;
  /** Heading depth (1 = top-level). */
  level: number;
  /** True when this heading (or any descendant heading) has prose beneath it. */
  has_content: boolean;
  /** The section's own prose (paragraphs directly beneath this heading), if any.
   *  Sent at import and persisted server-side as the section's auditable SSOT;
   *  the server strips it off the lean ``custom_spine`` it stores. Omitted for a
   *  pure parent heading with no direct prose. */
  content?: string;
  /** True for the synthetic leading "Title Page" row — the document's
   *  title-page block (title line + confidentiality + metadata table) captured
   *  before the first heading. Restored as leading chrome on Load. */
  is_title?: boolean;
  /** Structured title-page fields (label→value, in document order) captured from
   *  the title block's 2-column metadata table. Only set on the ``is_title`` row.
   *  Sent at import and persisted server-side onto the title ``ModuleRun``'s
   *  ``title_fields`` (exactly like a drafted title page) so Load can repaint it
   *  through the borderless title-page renderer instead of a bordered markdown
   *  table. The server strips it off the lean ``custom_spine`` it stores. */
  title_fields?: Record<string, string>;
}

// Doc type config from backend
export interface DocTypeConfig {
  document_type: string;
  display_name: string;
  standard: string;
  skeleton: [string, string, number][];
  /** Heading-numbering mode from the doc-type YAML. "none" → render section
   *  headings as plain titles (no "3.2.P.1" prefix, no native list numbering);
   *  the number is retained as metadata + ASTHRA_SEC_* bookmark. Absent/"auto"
   *  → the default native-or-literal numbering. "template" (private
   *  workspace templates) → numbers exactly as the template authored them:
   *  literal "N  Title", title-only for each of `unnumbered_sections`. */
  heading_numbering?: "auto" | "none" | "template";
  /** Sections drawn WITHOUT a number under `heading_numbering: "template"`;
   *  their section number is an internal id (spine identity, bookmark). */
  unnumbered_sections?: string[];
  /** The submission type a private workspace template was imported as; null
   *  for a public doctype. */
  base_doc_type?: string | null;
  title_page: Record<string, unknown>;
  /** ``dependencies``: module ids whose drafted text this module reads as
   *  context (YAML ``dependencies``); the picker pre-ticks their sections. */
  modules: Record<string, { sections: string[]; title: string; dependencies?: string[] }>;
  gates: Array<{ name: string; display_name: string; modules: string[] }>;
  /** The eCTD-leaf partition: which sections compose each separately filed
   *  document. `null`/absent means this doctype is filed as ONE document —
   *  CSR, PROTOCOL_DRUG, PSUR and most others — and every leaf-aware branch
   *  keys off that, so those doctypes behave exactly as before leaves existed.
   *  Resolved SERVER-side by `csr_sim.doc_types.documents.effective_documents`
   *  and never re-derived here. CTD Module 2 is 15 of these; Module 3 is 65. */
  documents?: Array<{ id: string; title: string; sections: string[] }> | null;
  /** Pre-split leaves still resolvable for documents that stored one; never
   *  offered by the picker (see `shared/client/leafScope.ts`). */
  legacy_documents?: Array<{ id: string; title: string; sections: string[] }> | null;
  /** WHICH module is this doctype's title page — the cover of every rendered
   *  document and a member of no leaf. Served so the client never
   *  re-implements title detection as a string match. */
  title_module_id?: string | null;
  regulations: Record<string, string>;
  workflow: string[];
  /** Generation-phase config (from the doc-type YAML's `generation:` block).
   *  Exposes `insertion_mode`, which the SERVER reads as the per-doctype
   *  default in `_resolve_effective_insertion_mode`.
   *
   *  The add-in no longer branches on it. It pins
   *  `insertion_mode_override: "batch"` on every run it starts (for pane-close
   *  durability), and projects sections into the live document on every
   *  doc-type — see `shouldLiveProject` in variant-c/liveProjection.ts. */
  generation?: {
    insertion_mode?: "batch" | "section";
    [key: string]: unknown;
  };
  /** Plan-mode config from the doc-type YAML's `plan_mode:` block. */
  plan_mode?: {
    enabled?: boolean;
    require_approval?: boolean;
    [key: string]: unknown;
  };
  /** Shared formatting contract (from the server's `live_doc_style_spec.json`).
   *  Used to normalize the live Word session so a Loaded doc renders
   *  pixel-identically to the Downloaded .docx. Optional for backward
   *  compatibility with older servers that don't emit it. */
  live_doc_style_spec?: LiveDocStyleSpec;
}

/** Mirror of `src/csr_sim/live_doc_style_spec.json`. Units are explicit:
 *  `*_pt` = points, `*_in` = inches, `color` = 6-digit hex RRGGBB.
 *
 *  For a private workspace template with captured formatting the server sends
 *  the global spec overridden role by role by the client's template
 *  (`csr_sim.template_style`); the optional fields below appear only then. */
export interface LiveDocStyleSpec {
  normal: {
    font: string;
    size_pt: number;
    space_after_pt: number;
    /** EXACT line spacing in points (not a multiple) — Office.js
     *  `paragraphFormat.lineSpacing` is points-only. */
    line_spacing_pt: number;
    space_before_pt?: number;
    color?: string;
    alignment?: "left" | "center" | "right" | "justified";
    /** The client's own "Multiple" rule; `line_spacing_pt` = multiple × 12. */
    line_spacing_multiple?: number;
  };
  heading_common: {
    font: string;
    bold: boolean;
    color: string;
    space_after_pt: number;
  };
  /** Per-level `font` / `bold` / `italic` / `color` / `space_after_pt`, when
   *  present, override `heading_common` for that level (client templates). */
  headings: Array<{
    level: number;
    size_pt: number;
    space_before_pt: number;
    font?: string;
    bold?: boolean;
    italic?: boolean;
    color?: string;
    space_after_pt?: number;
  }>;
  page: {
    orientation: "portrait" | "landscape";
    width_in: number;
    height_in: number;
    margin_in: number;
  };
  /** `source: "template"`: the client template's own header / footer is used;
   *  the add-in leaves it as inserted instead of writing the Asthra one. */
  header: {
    size_pt: number;
    color: string;
    alignment: "left" | "center" | "right";
    source?: "template";
  };
  footer: {
    size_pt: number;
    color: string;
    alignment: "left" | "center" | "right";
    source?: "template";
  };
  /** Narrative table look (older servers omit it). */
  table?: {
    style?: string;
    font_size_pt?: number;
    header_fill?: string;
    header_bold?: boolean;
  };
  /** Present only when a client template overrides the global spec. */
  _template?: { overrides: string[]; source?: string | null };
}

/** One ``GET /doc-types`` entry. Private workspace templates (``private: true``)
 *  appear only when a ``workspaceId`` is passed and the caller belongs to it. */
export type DocTypeListEntry = {
  code: string;
  display_name: string;
  standard: string;
  version?: string;
  /** Whole-document price in minor units (USD cents), or null when not on sale.
   *  Sourced live from ``doctypes/doc_pricing.json`` via the loader. */
  document_price_minor?: number | null;
  /** Per-section price in minor units (USD cents), or null. */
  section_price_minor?: number | null;
  /** Whether this doctype is self-serve purchasable (on the pricing allowlist). */
  self_serve?: boolean;
  /** Submission regions a run may choose (code → description); {} when none are offered. */
  regions?: Record<string, string>;
  /** True for a private template linked to the requested workspace. */
  private?: boolean;
  /** A private template's submission type — the public doctype it inherits
   *  drafting and regulatory config from. */
  base_doc_type?: string | null;
  /** The workspace a private template was listed for. */
  workspace_id?: string;
  template_id?: string;
  /** Sections with drafting instructions (private templates only). */
  draftable_count?: number;
  section_count?: number;
};

/** The doctype catalogue. Pass ``workspaceId`` to also receive that workspace's
 *  private templates; omitted, the answer is the public catalogue only. */
export async function getDocTypes(workspaceId?: string | null): Promise<DocTypeListEntry[]> {
  const query = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
  return request("GET", `/doc-types${query}`);
}

/** A doctype's full config. For a private workspace template pass the
 *  workspace it was picked in, so access is checked there (the run-start rule)
 *  rather than across every workspace the caller belongs to. */
export async function getDocTypeConfig(docType: string, workspaceId?: string | null): Promise<DocTypeConfig> {
  const query = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
  return request("GET", `/doc-types/${docType}${query}`);
}

/** One source document a doctype drafts from, in writer language. */
export type ExpectedSource = {
  name: string;
  /** A few words on what the draft takes from it. */
  used_for: string;
  /** True for the documents most sections lean on. */
  core: boolean;
};

/** The source documents ``docType`` usually draws on, core first; ``[]`` when
 *  it declares none. A private template asks with its ``base_doc_type``. */
export async function getExpectedSources(docType: string): Promise<ExpectedSource[]> {
  const res = await request<{ doc_type: string; sources: ExpectedSource[] }>(
    "GET",
    `/doc-types/${encodeURIComponent(docType)}/expected-sources`,
  );
  return res.sources ?? [];
}

/** What a writer sees under a doctype's "i": the guidance drafting follows,
 *  what the drafting agent can do (in a writer's words), and the sources it
 *  expects. Read-only; built server-side from what drafting already uses. */
export type DoctypeAbout = {
  doc_type: string;
  guidance: { primary: string[]; secondary: string[] };
  tools: string[];
  sources: ExpectedSource[];
};

export async function getDoctypeAbout(docType: string): Promise<DoctypeAbout> {
  return request("GET", `/doc-types/${encodeURIComponent(docType)}/about`);
}

// ---------------------------------------------------------------------------
// Module instructions — for the section detail panel
// ---------------------------------------------------------------------------

export interface ModuleInstructions {
  doc_type: string;
  module_id: string;
  section_title: string;
  content_markdown: string;
}

/** Fetch the instruction text the agent is told to follow for one module.
 *  Today the backend only implements CER; CSR returns 501. The detail panel
 *  catches that and shows a friendly empty state. */
export async function getModuleInstructions(docType: string, moduleId: string): Promise<ModuleInstructions> {
  return request("GET", `/instructions/${docType}/${moduleId}`);
}

// ---------------------------------------------------------------------------
// Run provenance — raw per-module JSON, failures, analytics
// ---------------------------------------------------------------------------

/** Fetch the unparsed per-module JSON exactly as written to disk for a
 *  given run. Powers the Raw JSON tab in SectionDetailPanel. The backend
 *  returns 404 if the module hasn't completed yet (mid-run state) — the
 *  caller should treat 404 as "not yet available", not as a hard error. */
export async function getModuleRawJson(
  trial: string,
  session: string,
  moduleId: string,
): Promise<Record<string, unknown>> {
  return request(
    "GET",
    `/runs/${encodeURIComponent(trial)}/${encodeURIComponent(session)}/modules/${encodeURIComponent(moduleId)}/raw`,
  );
}

// ---------------------------------------------------------------------------
// Section source attributions
// ---------------------------------------------------------------------------

/** One source document the agent retrieved to draft a section. */
export interface SourceAttribution {
  file_label: string;
  section_label: string;
  tool: string;
  seq: number;
  transaction_id: string;
  classification: string;
  content_length: number;
}

/** Response from /api/runs/{trial}/{session}/sections/{section}/sources. */
export interface SectionSourcesResponse {
  section_number: string;
  module_id: string | null;
  source_count: number;
  sources: SourceAttribution[];
}

/** Fetch the source attributions for a section in a specific run. */
export async function getSectionSources(
  trial: string,
  session: string,
  sectionNumber: string,
  docType = "CER",
): Promise<SectionSourcesResponse> {
  return request(
    "GET",
    `/runs/${encodeURIComponent(trial)}/${encodeURIComponent(session)}/sections/${encodeURIComponent(sectionNumber)}/sources?doc_type=${encodeURIComponent(docType)}`,
  );
}

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

/** One source reference linking drafted text to a source document. */
export interface Citation {
  citation_number: number;
  document_name: string;
  section_name: string;
  page_no: number;
  context: string;
  transaction_id: string;
}

/** One step of a computed number's trace: the agent's reasoning, an
 *  artefact operation, the method, or the result. ``input_refs`` name
 *  inputs, and ``citation_numbers`` maps each to its input citation. */
export interface DerivationTraceStep {
  kind: "reason" | "step" | "method" | "result";
  text: string;
  input_refs?: string[];
  citation_numbers?: Record<string, number>;
}

/** One input to a computed number and the source it was read from. */
export interface DerivationInput {
  name: string;
  value?: unknown;
  /** source | design | unsourced (calculate); ref | artefact | note | literal (create_artefact). */
  kind: string;
  source: string;
  verified: boolean;
  reason: string;
  citation: Citation | null;
}

/** "How this was calculated" for a computed number in the selection. */
export interface CitationDerivation {
  transaction_id: string;
  kind: "calculate" | "artefact" | string;
  operation: string;
  method: string;
  value: string;
  unit: string;
  rationale: string;
  /** What the agent wrote just before running the calculation, when it wrote anything. */
  agent_note?: string;
  matched_values: string[];
  /** Every input traced to its source (or is a design parameter). */
  verified: boolean;
  trace: DerivationTraceStep[];
  inputs: DerivationInput[];
  /** Which arithmetic actually ran (audit detail kept out of ``method``). */
  implementation?: string;
  /** Anything the calculator raised that a reviewer must see. */
  notes?: string[];
  /** The code that ran it: module, function, code version, library versions. */
  software?: DerivationSoftware;
}

/** Which code computed a number (``csr_sim.derivation.software``). */
export interface DerivationSoftware {
  module?: string;
  function?: string;
  code_version?: string;
  libraries?: Record<string, string>;
}

/** Response from POST /api/citations. */
export interface CitationResponse {
  citations: Citation[];
  section_number: string;
  selected_text_preview: string;
  source_count: number;
  status: "ok" | "no_provenance" | "upstream_error" | "partial";
  message: string | null;
  /** Asthra transaction IDs the server queried against. Empty when
   *  the section has no asthra_utils retrieval calls in its
   *  provenance ledger. Surfaced so the addin can distinguish "no
   *  sources to look up" (length 0) from "sources searched, none
   *  matched" (length > 0 but citations empty). */
  transaction_ids_used?: string[];
  /** Section numbers known to the doc type, surfaced by the server
   *  only when ``status === "no_provenance"``. Lets the addin show
   *  "section X doesn't exist for this doc type" with the actual
   *  catalog rather than a generic miss. Undefined / empty on
   *  successful resolutions. */
  valid_sections?: string[];
  /** Computed numbers in the selection, each with its trace and cited
   *  inputs. Absent on servers that predate it. */
  derivations?: CitationDerivation[];
}

/** Resolve selected text to source citations via Asthra.
 *
 *  `doc_type` is required: the server maps ``(section_number,
 *  doc_type)`` → module_id, and a wrong doc_type silently misses
 *  every module (e.g. asking for section 3 with doc_type="CER"
 *  on a PSUR run looks for "CER-03" which doesn't exist —
 *  status comes back as no match, writer sees "no references").
 *  The prior fallback to "CER" was dark code; removed so callers
 *  who forget to pass doc_type get a TypeScript error, not a
 *  silent wrong-answer path.
 */
export async function getCitations(params: {
  selected_text: string;
  section_number: string;
  trial: string;
  session: string;
  doc_type: string;
}): Promise<CitationResponse> {
  return request("POST", "/citations", params);
}

/** A single recent run as returned by /api/runs. Mirrors the
 *  RunMetadata dataclass on the backend (server/provenance/store.py). */
export interface RecentRun {
  trial: string;
  session: string;
  started_at: string | null;
  completed_at: string | null;
  total_modules: number;
  completed_modules: number;
  failed_modules: number;
}

/** Fetch the N most-recent runs across all trials, newest first. Used
 *  by the Analytics tab's run history selector. */
export async function getRecentRuns(limit = 50): Promise<RecentRun[]> {
  return request("GET", `/runs?limit=${limit}`);
}

/** A single failed tool call as returned by /api/runs/.../failures.
 *  Includes the full params dict (the actual call parameters that
 *  failed) so the Errors panel can display the exact query the agent
 *  sent — addresses the user's specific request: "for API failures,
 *  show the actual call that failed including parameters". */
export interface FailureEntry {
  module_id: string;
  seq: number;
  tool: string;
  params: Record<string, unknown>;
  transaction_id: string;
  timestamp: string;
  error_message: string;
  classification: string; // "exception" | "no_data" | "loop" | "other"
}

/** Fetch all failed tool calls for a run. */
export async function getRunFailures(trial: string, session: string): Promise<FailureEntry[]> {
  return request("GET", `/runs/${encodeURIComponent(trial)}/${encodeURIComponent(session)}/failures`);
}

/** Per-module breakdown row in /api/runs/.../analytics. */
export interface ModuleAnalyticsRow {
  module_id: string;
  title: string;
  duration_seconds: number;
  tool_call_count: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  draft_chars: number;
  thinking_trace_count: number;
  transaction_count: number;
}

/** Aggregated analytics returned by /api/runs/.../analytics. One
 *  fetch powers the entire Analytics tab — see route docstring in
 *  server/routes/run_provenance.py. */
export interface RunAnalytics {
  metadata: RecentRun;
  aggregate: {
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
  };
  modules: ModuleAnalyticsRow[];
  failure_count: number;
  failure_summary: Record<string, number>;
  tool_frequency: Record<string, number>;
}

export async function getRunAnalytics(trial: string, session: string): Promise<RunAnalytics> {
  return request("GET", `/runs/${encodeURIComponent(trial)}/${encodeURIComponent(session)}/analytics`);
}

// ---------------------------------------------------------------------------
// Runtime sandbox-mode toggle
// ---------------------------------------------------------------------------

export interface AppConfig {
  preset: string;
  label: string;
  base_model: string;
  /** Spec-stage model — used for the lightweight Specification gate. */
  spec_model?: string;
  /** Reviewer model — used by the per-module reviewer when enabled. */
  reviewer_model?: string;
  /** Thinking budget for base/spec models (tokens). 0 = no thinking. */
  base_thinking?: number;
  spec_thinking?: number;
  sandbox: boolean;
  sandbox_available_doc_types: string[];
  /** Runtime plan-mode override: null means "use the doc-type YAML's
   *  plan_mode.enabled setting"; true/false forces plan mode on/off
   *  regardless of YAML. The frontend combines this with the doc-type
   *  config to compute the effective behaviour. */
  plan_mode_override: boolean | null;
  /** Filesystem path of the central log directory ($ASTHRA_LOG_DIR or
   *  /tmp/asthra_logs). Displayed in the Settings tab so the writer can
   *  find asthra.log on disk. */
  log_dir?: string;
}

/** Fetch the runtime app config — sandbox flag, model preset, available
 *  fixture catalogs. Called on app boot to render the SANDBOX/LIVE badge,
 *  and re-called after a sandbox-mode flip to refresh it. */
export async function getAppConfig(): Promise<AppConfig> {
  return request("GET", "/config");
}

/** Flip the runtime sandbox-mode flag. Backend refuses (409) if any
 *  pipeline run is currently active. Returns the new state on success. */
export async function setSandboxMode(enabled: boolean): Promise<{ enabled: boolean }> {
  return request("POST", "/sandbox-mode", { enabled });
}

// --- LLM model backend (provider) selection --------------------------------
// Each provider ("backend") defines a small + big model. The signed-in user
// picks one and it serves every task. Read: GET /user-config/backends;
// write: PUT /user-config/me { provider }. Any authenticated user may change
// their own (not admin-gated). Mirrors guru-web's BackendControl contract.

export interface BackendModel {
  /** Preset-table alias, e.g. "haiku". */
  alias: string;
  /** Resolved model id, e.g. "openai/gpt-5.6-luna". */
  model: string;
}
export interface BackendRow {
  /** Provider key committed by the picker (anthropic/openai/…/openrouter). */
  provider: string;
  small?: BackendModel;
  big?: BackendModel;
}
export interface BackendsState {
  backends: BackendRow[];
  /** The provider the caller resolves to today. */
  current: string;
}
export interface MyModelConfig {
  username: string;
  provider: string;
  preset: string;
}

/** List selectable backends (with small/big models) + the caller's current one. */
export async function getBackends(): Promise<BackendsState> {
  return request("GET", "/user-config/backends");
}

/** Set the caller's own model backend (provider). Resolves to the new config. */
export async function setBackend(provider: string): Promise<MyModelConfig> {
  return request("PUT", "/user-config/me", { provider });
}

/** Set the runtime plan-mode override.
 *  Pass `enabled: true` to force plan mode ON, `enabled: false` to
 *  force it OFF, or `enabled: null` to clear the override and fall
 *  back to the doc-type YAML's plan_mode.enabled setting.
 *  No active-run guard — flipping mid-run is harmless because plan
 *  mode is only checked at /api/plan request time. */
export async function setPlanMode(enabled: boolean | null): Promise<{ enabled: boolean | null }> {
  return request("POST", "/plan-mode", { enabled });
}

export async function startRun(params: StartRunParams): Promise<{ run_id: string }> {
  return request("POST", "/runs", params);
}

/** Set (or clear) a draft's user-chosen display name.
 *
 *  Writes ``DraftRunDoc.name`` and mirrors onto the completed-run index, so the
 *  new name shows in both the live and completed Drafts/Home lists. Pass an
 *  empty string to clear the name (falls back to the synthesized project name). */
export async function renameRun(runId: string, name: string): Promise<{ run_id: string; name: string | null }> {
  return request("PATCH", `/runs/${runId}`, { name });
}

export interface ContinueRunSectionsParams {
  module_ids: string[];
  plan_id?: string;
  /** The writer's final comments for THIS batch; omitted clears the run's previous ones. */
  run_instructions?: string;
}

export async function continueRunSections(
  runId: string,
  params: ContinueRunSectionsParams,
): Promise<{
  run_id: string;
  status: string;
  added_module_ids: string[];
  scoped_module_ids: string[];
  /** Stream position captured just before dispatch — subscribe from here to
   *  replay this dispatch's first events (its module_start included). */
  stream_cursor?: string | null;
}> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/continue-sections`, params);
}

/**
 * Draft one or more CUSTOM-submission sections using their writer-authored
 * per-section instructions. The custom analogue of {@link continueRunSections}:
 * ``continue-sections`` only accepts doctype-template module ids, but custom
 * headings (``CUSTOM-N``) live in a synthetic gate with no template instruction,
 * so they drive the engine from the instruction persisted on the ``custom_spine``
 * row instead. Reuses the run's identity + SSE stream. Every ``module_ids`` entry
 * must have an instruction saved (the caller gates this via the spine row).
 */
export async function customDraftSections(
  runId: string,
  params: ContinueRunSectionsParams,
): Promise<{
  run_id: string;
  status: string;
  added_module_ids: string[];
}> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/custom-draft`, params);
}

export async function getRunStatus(runId: string): Promise<import("../types").RunStatus> {
  return request("GET", `/runs/${runId}`);
}

export async function getSectionContent(runId: string, moduleId: string): Promise<import("../types").SectionResponse> {
  return request("GET", `/runs/${runId}/sections/${moduleId}`);
}

export interface SectionFragment {
  section_number: string;
  /** Body-only .docx, base64. Null when the chunk had nothing to render. */
  docx_base64: string | null;
}

/** Render a module's already-split body chunks as .docx fragments on the
 *  server, so each can go into Word with one ``insertFileFromBase64`` instead
 *  of the per-paragraph render. Fragments come back in chunk order. */
export async function renderSectionFragments(
  runId: string,
  moduleId: string,
  chunks: Array<{ section_number: string; markdown: string }>,
): Promise<{ module_id: string; fragments: SectionFragment[] }> {
  return request("POST", `/runs/${runId}/sections/${moduleId}/fragment`, { chunks });
}

/** The run's document as "Load doc" renders it before anything is drafted
 *  (title page, headings, page setup, header / footer, the client template's
 *  styles; no section bodies), as a .docx (base64). The live-drafting scaffold
 *  of a client template. ``docx_base64`` is null when the server can't render
 *  it — the add-in then builds its own scaffold. */
export async function renderRunSkeleton(runId: string): Promise<{ docx_base64: string | null }> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/skeleton`, {});
}

/** A private template's own title page as a .docx (base64), filled with
 *  ``fields`` (a run's title_fields; ``{}`` for the empty template page).
 *  ``docx_base64`` is null when the doctype has no template title page. */
export async function renderTemplateCover(
  docType: string,
  fields: Record<string, string>,
): Promise<{ docx_base64: string | null }> {
  return request("POST", `/doc-types/${encodeURIComponent(docType)}/cover`, { fields });
}

/** One ordered slice of the instruction markdown served to the drafter.
 *  "conditional" segments carry the plain-English activation condition and
 *  the resolver's verdict; FALSE segments were EXCLUDED from the served
 *  text but their content is preserved here for inspection. */
export interface InstructionSegment {
  kind: "text" | "conditional";
  condition: string | null;
  verdict: "TRUE" | "FALSE" | "UNKNOWN" | "UNRESOLVED" | null;
  content: string;
}

export interface InstructionsUsedPayload {
  module_id: string;
  doc_type: string;
  instruction_section: string | null;
  resolver_mode: string | null;
  module_activation: { condition: string; verdict: string } | null;
  segments: InstructionSegment[];
  full_text_length: number;
  served_at: string | null;
}

export interface SectionInstructionsResponse {
  module_id: string;
  available: boolean;
  instructions_used: InstructionsUsedPayload | null;
}

/** Final instruction text used to draft one section, with per-conditional-
 *  block activation verdicts. `available: false` on runs recorded before
 *  instruction capture existed. */
export async function getSectionInstructions(runId: string, moduleId: string): Promise<SectionInstructionsResponse> {
  return request("GET", `/runs/${runId}/sections/${moduleId}/instructions`);
}

// Action History wire types + the pure tree builder live in the cross-surface
// module (`shared/client/actionHistory.ts`) so the add-in and the web app render
// the same hierarchy from the same payload. Re-exported here so existing
// imports keep working.
export type {
  ActionHistoryPayload,
  ActionTreeNode,
  ActivityEvent,
  AgentSpanNode,
  AttemptInfo,
  InternetCall,
  LibraryCall,
  OtherCall,
  PlanNode,
  TimelineEvent,
  WebResult,
} from "@shared/client/actionHistory";
import type { ActionHistoryPayload } from "@shared/client/actionHistory";

/** Context-preparation tool calls made for one section, grouped into internet
 *  vs. local-library lists. `available: false` on runs recorded before
 *  tool-call capture existed, or sections not yet drafted. */
export async function getSectionActionHistory(
  runId: string,
  moduleId: string,
  attempt?: number,
): Promise<ActionHistoryPayload> {
  const qs = attempt != null ? `?attempt=${encodeURIComponent(String(attempt))}` : "";
  return request("GET", `/runs/${runId}/sections/${moduleId}/action-history${qs}`);
}

/** Cancel a run. Resolves with the backend's terminal status
 *  (``{ run_id, status: "cancelled" }``) so the caller can confirm the
 *  server actually accepted the stop. Rejects with an ``Error & { status }``
 *  on any non-2xx — notably ``status === 404`` means the run is no longer
 *  active (already stopped), which the caller should treat as success. */
export async function cancelRun(runId: string): Promise<{ run_id: string; status: string }> {
  return request("DELETE", `/runs/${runId}`);
}

/**
 * Regenerate one section inside an EXISTING run. Re-uses the original
 * ``run_id`` — no new run is created, no new SSE stream is opened. The
 * backend resets the section's SSOT to ``queued``, then dispatches the
 * same gate runner the original ``/runs`` POST does, scoped to one
 * module + its gate. Terminal SSE event is ``regen_complete`` (not
 * ``pipeline_complete``) so the existing EventSource stays open.
 */
/** What a regenerate's "use only X" came to: ``restricted`` with no
 *  ``documents`` means nothing in the library matched, so the whole library
 *  is read. Absent on older servers. */
export interface RegenSourceScope {
  restricted: boolean;
  documents: string[];
}

export async function regenerateSection(
  runId: string,
  moduleId: string,
  options?: {
    guidance?: string | null;
    source?: "inspector" | "chat_command" | "chat_agent" | "system";
    requestedBy?: string | null;
    comments?: Array<Record<string, unknown>>;
  },
): Promise<{
  run_id: string;
  status: string;
  module_id: string;
  source_scope?: RegenSourceScope;
  /** Stream tail captured before the regen's first event — subscribe from here. */
  stream_cursor?: string | null;
}> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/regenerate`,
    options
      ? {
          guidance: options.guidance ?? null,
          source: options.source ?? "system",
          requested_by: options.requestedBy ?? null,
          comments: options.comments ?? [],
        }
      : undefined,
  );
}

/**
 * Full-section REDRAFT inside an existing run (the ``/regen`` command). Runs
 * the plan→retrieve→draft pipeline with ``instruction`` as ADDITIVE change
 * guidance on top of the configured module instructions, and holds the result
 * as a PROPOSAL: the section SSOT is untouched, the draft arrives on the run
 * stream as ``adhoc_draft_ready``, and the terminal SSE marker is the
 * non-terminal ``adhoc_draft_complete`` (EventSource stays open).
 *
 * Chat "draft content" does NOT come here — redrafting a whole section is a
 * run, and asking for one paragraph should not be. See
 * ``requestFragmentDraftStream``.
 */
export async function adhocDraftSection(
  runId: string,
  moduleId: string,
  options: {
    instruction: string;
    requestedBy?: string | null;
    /** Writer-attached source assets (@-mentions) — resolved server-side
     *  at the route and injected into the drafting agent's context. */
    attachments?: MentionAttachment[];
  },
): Promise<{ run_id: string; status: string; module_id: string; source_scope?: RegenSourceScope }> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/adhoc-draft`, {
    instruction: options.instruction,
    mode: "redraft",
    source: "chat_intent",
    requested_by: options.requestedBy ?? null,
    attachments: (options.attachments ?? []).map(toWireAttachment),
  });
}

/** Request body for ``requestFragmentDraftStream``. */
export interface FragmentDraftRequestBody {
  instruction: string;
  /** Text of the paragraph the writer's cursor is in; the fragment is
   *  inserted after it. Null appends to the end of the section. */
  anchorText?: string | null;
  sectionNumber?: string | null;
  docType?: string | null;
  libraryId?: string | null;
  attachments?: MentionAttachment[];
}

/** Terminal ``complete`` event of the fragment-draft stream. */
export interface FragmentDraftResult {
  content: string;
  run_id: string;
  module_id: string;
  section_number: string | null;
  section_version: number | null;
  anchor_text: string | null;
  instruction: string;
  word_count: number;
  /** False when the proposal could not be parked server-side — the card still
   *  shows the draft, but a pane reload will not bring it back. */
  persisted: boolean;
  retrieval_provenance?: Record<string, unknown>[];
  tool_call_log?: Record<string, unknown>[];
  root_span_id?: string | null;
}

/**
 * Chat "draft content": draft ONE PIECE of new content for a section, streamed.
 *
 * Deliberately NOT a run. The old path enqueued a pipeline job to write a
 * paragraph — subprocess, library tree preload, scaffold .docx, run-status
 * flip, collaborator notifications — all of it per-RUN setup for drafting a
 * SECTION. This is one in-request agent call instead, the same shape
 * ``requestSelectionEditStream`` uses for the highlighted-span rewrite.
 *
 * Aborting the signal drops the connection; the route polls
 * ``request.is_disconnected`` and stops the agent at its next tool boundary.
 * That is the Stop button — there is no run to cancel.
 */
export async function requestFragmentDraftStream(
  runId: string,
  moduleId: string,
  body: FragmentDraftRequestBody,
  onEvent: (event: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getSessionToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/fragment-draft/stream`,
    {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({
        instruction: body.instruction,
        anchor_text: body.anchorText ?? null,
        section_number: body.sectionNumber ?? null,
        doc_type: body.docType ?? null,
        library_id: body.libraryId ?? null,
        attachments: (body.attachments ?? []).map(toWireAttachment),
      }),
      signal,
    },
  );

  if (!resp.ok || !resp.body) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (line.startsWith("data: ")) {
        try {
          onEvent(JSON.parse(line.slice(6)));
        } catch {
          // Skip malformed events
        }
      }
    }
  }
}

/**
 * Save (or clear) the per-section drafting instruction for one heading of a
 * CUSTOM submission. Custom sections are the writer's own document headings and
 * carry no doctype-template instruction; this writer-authored text is what the
 * drafter follows (and gates whether the section can be drafted at all). Pass an
 * empty string to clear. Persisted on the ``custom_spine`` row; the returned
 * ``instruction`` is the trimmed value (or null when cleared).
 */
export async function saveCustomSectionInstruction(
  runId: string,
  moduleId: string,
  instruction: string,
): Promise<{ run_id: string; module_id: string; instruction: string | null }> {
  return request(
    "PUT",
    `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/custom-instruction`,
    { instruction },
  );
}

/** Stop an in-flight ad-hoc draft for ONE section, from the composer's Stop
 *  button. Deliberately NOT ``cancelRun`` — that arms ``cancel:{run_id}``, which
 *  SIGKILLs the run child and flips the run's durable status to "cancelled",
 *  taking down work the writer never asked to stop. This arms a module-scoped
 *  latch instead; the run stays alive and dispatchable.
 *
 *  Idempotent: a draft that already finished returns ok:false rather than
 *  throwing, so a late click is a no-op. */
export async function cancelAdhocDraft(
  runId: string,
  moduleId: string,
): Promise<{ ok: boolean; run_id: string; module_id: string }> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/adhoc-draft/cancel`,
  );
}

/** Discriminated result of ``decideAdhocProposal`` — the two 409 shapes
 *  (version drift, proposal already decided/replaced) are surfaced as
 *  data, not thrown, so the card can react without try/catch gymnastics. */
export type AdhocProposalDecisionResult =
  | { kind: "approved"; sectionVersion: number; content: string; anchorMissed: boolean }
  | { kind: "rejected" }
  | { kind: "version_conflict"; currentVersion: number; currentContent: string | null }
  | { kind: "no_pending_proposal" }
  | { kind: "error"; message: string };

/** Approve/reject a pending ad-hoc draft proposal. Approve is the
 *  SSOT-first half of the apply: the server walks the lifecycle, lands
 *  the body via the version-checked edit path, and stamps the proposal
 *  decided — the caller writes into Word only after a ``kind: "approved"``
 *  result.
 *
 *  What ``content`` means follows the proposal's mode: a redraft sends the
 *  whole replacement body, a draft sends only the FRAGMENT and the server
 *  splices it in after ``anchorText``. ``anchorMissed`` comes back true when
 *  that paragraph was gone and the fragment was appended instead. */
export async function decideAdhocProposal(
  runId: string,
  moduleId: string,
  body: {
    action: "approve" | "reject";
    expectedVersion?: number | null;
    content?: string;
    anchorText?: string | null;
  },
): Promise<AdhocProposalDecisionResult> {
  try {
    const res = await request<{
      ok: boolean;
      decision: "approved" | "rejected";
      section_version?: number;
      content?: string;
      anchor_missed?: boolean;
    }>("POST", `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/adhoc-proposal/decision`, {
      action: body.action,
      expected_version: body.expectedVersion ?? null,
      content: body.content ?? null,
      anchor_text: body.anchorText ?? null,
    });
    if (res.decision === "approved") {
      return {
        kind: "approved",
        sectionVersion: res.section_version ?? 0,
        content: res.content ?? "",
        anchorMissed: Boolean(res.anchor_missed),
      };
    }
    return { kind: "rejected" };
  } catch (err) {
    const detail = (err as { detail?: unknown }).detail;
    if (detail && typeof detail === "object") {
      const d = detail as { error?: string; current_version?: number; current_content?: string | null };
      if (d.error === "version_conflict" && typeof d.current_version === "number") {
        return {
          kind: "version_conflict",
          currentVersion: d.current_version,
          currentContent: d.current_content ?? null,
        };
      }
      if (d.error === "no_pending_proposal") {
        return { kind: "no_pending_proposal" };
      }
    }
    return { kind: "error", message: err instanceof Error ? err.message : String(err) };
  }
}

// ---------------------------------------------------------------------------
// Chat intent routing
// ---------------------------------------------------------------------------

export type ChatRoutedIntent = "chat" | "draft_content" | "edit_highlight" | "research_highlight";

export interface ChatIntentRequestBody {
  message: string;
  has_highlight: boolean;
  highlight_preview?: string | null;
  cursor_section_number?: string | null;
  cursor_section_title?: string | null;
  can_target_section: boolean;
  doc_type?: string | null;
  /** Correlation id — generated per classify call, echoed by the
   *  server in its response + logs. Filled in by classifyChatIntent. */
  request_id?: string;
}

export interface ChatIntentResponse {
  intent: ChatRoutedIntent;
  classified_intent?: ChatRoutedIntent | null;
  confidence?: "high" | "low" | null;
  reason: string;
  request_id?: string | null;
}

const CHAT_INTENT_TIMEOUT_MS = 5000;

function newIntentRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Older webviews without crypto.randomUUID — cheap fallback.
    return `ci-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Classify a chat submission into a routable intent. NEVER throws and
 * never blocks past ~5s — any failure or timeout resolves to plain
 * ``chat`` so the writer's message always lands somewhere.
 */
export async function classifyChatIntent(body: ChatIntentRequestBody): Promise<ChatIntentResponse> {
  const requestId = body.request_id ?? newIntentRequestId();
  const fallback: ChatIntentResponse = {
    intent: "chat",
    reason: "client_error",
    request_id: requestId,
  };
  try {
    const timeout = new Promise<ChatIntentResponse>((resolve) => {
      setTimeout(() => {
        // Logged (not silent) so a server-side match that arrived too
        // late can be joined with this abandonment via request_id.
        console.warn(`[chatIntent] classification timed out after ${CHAT_INTENT_TIMEOUT_MS}ms`, { requestId });
        resolve({ intent: "chat", reason: "client_timeout", request_id: requestId });
      }, CHAT_INTENT_TIMEOUT_MS);
    });
    return await Promise.race([
      request<ChatIntentResponse>("POST", "/chat-intent", { ...body, request_id: requestId }),
      timeout,
    ]);
  } catch (err) {
    console.warn("[chatIntent] classification failed — falling back to chat", { requestId, err });
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// Selection edit (chat "edit highlight" intent)
// ---------------------------------------------------------------------------

/** One retrieval transaction produced while resolving the rewrite plan.
 *  Round-trips through the client so the Approve write-back can append it
 *  to the section's provenance — persisted only if the writer approves. */
export interface RetrievalProvenanceItem {
  transaction_id: string;
  tool_name: string;
  params: Record<string, unknown>;
  content: string;
  content_length: number;
  timestamp: string;
}

export interface SelectionEditResponse {
  replacement_text: string;
  original_text: string;
  module_id: string | null;
  section_number: string | null;
  /** Section version captured server-side — pass as expected_version on write-back. */
  section_version: number | null;
  /** 0 = SSOT drift (selection not found in stored draft); >1 = ambiguous. */
  occurrences_in_section: number;
  /** Full stored section draft, captured atomically with section_version.
   *  The Approve write-back computes the new content from this text. */
  section_draft: string | null;
  /** Retrieval transactions that fed the rewrite (streaming flow only).
   *  Passed back on Approve so they land on the section's provenance. */
  retrieval_provenance?: RetrievalProvenanceItem[];
  /** The rewrite agent's tool-call log (ledger projection) + its root span.
   *  Passed back on Approve so the calls land on the section's Action History
   *  under an "Approved the suggested rewrite" branch. */
  tool_call_log?: Record<string, unknown>[];
  root_span_id?: string | null;
  /** Compact retrieval outcome for this rewrite: how many planned lookups
   *  returned content and the cascade's "still missing" gap notes. Used to
   *  explain WHY a table edit produced no change (data genuinely absent vs.
   *  rows already matching). */
  retrieval_summary?: RetrievalSummary | null;
}

/** Server-side summary of what the rewrite's retrieval actually found. */
export interface RetrievalSummary {
  /** Number of planned retrieval items. */
  items: number;
  /** How many of them returned usable content. */
  found: number;
  /** Cascade "what's still missing" notes (may be empty). */
  gaps: string[];
}

export interface SelectionEditRequestBody {
  selected_text: string;
  instruction: string;
  run_id?: string | null;
  module_id?: string | null;
  section_number?: string | null;
  doc_type?: string | null;
  /** Revision pass — Asthra's own prior rewrite of this span that the writer
   *  reviewed and asked to change further (proposal card "Revise"). When set,
   *  ``selected_text`` stays the ORIGINAL span so occurrences / write-back
   *  still target the live document, while the rewrite refines this draft. */
  previous_draft?: string | null;
  /** Library to resolve attachments against (no tool loop on this endpoint). */
  library_id?: string | null;
  /** Writer-attached source assets (@-mentions) — pre-fetched server-side
   *  and embedded in the rewrite prompt. */
  attachments?: MentionAttachment[];
  /** A canned "resolve the flags" request, not a free-form rewrite: when every
   *  flag in the span is a fill-in placeholder the server fills it value-only
   *  (same as the Fix-issues pass). */
  resolve_flags?: boolean;
}

export async function requestSelectionEdit(body: SelectionEditRequestBody): Promise<SelectionEditResponse> {
  const { attachments, ...rest } = body;
  return request("POST", "/selection-edit", {
    ...rest,
    attachments: (attachments ?? []).map(toWireAttachment),
  });
}

/**
 * Streaming rewrite — POST /selection-edit/stream. Emits the same SSE
 * events the chat flows do (status / plan_ready / task_start /
 * retrieval_step / task_done / task_failed / drafting_response / complete /
 * error) so the UI can show the rewrite checking for, retrieving, and
 * applying reference context. On the default agent-loop path, ``task_start``
 * carries the query under ``need`` (not ``title``) and ``task_done`` carries
 * ``via``/``level``; ``labelForSelectionEditEvent`` maps these to the status
 * line. The terminal ``complete`` event carries the full
 * SelectionEditResponse payload.
 * Never resolves the response itself — the caller assembles it from the
 * ``complete`` event via ``onEvent``.
 */
export async function requestSelectionEditStream(
  body: SelectionEditRequestBody,
  onEvent: (event: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const { attachments, ...rest } = body;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getSessionToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(`${API_BASE}/selection-edit/stream`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({
      ...rest,
      attachments: (attachments ?? []).map(toWireAttachment),
    }),
    // Aborting drops the connection; the route polls ``request.is_disconnected``
    // every 0.5s and sets a cooperative cancel event, so the agent loop really
    // stops rather than finishing at the writer's expense.
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (line.startsWith("data: ")) {
        try {
          onEvent(JSON.parse(line.slice(6)));
        } catch {
          // Skip malformed events
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Regulatory audit
// ---------------------------------------------------------------------------

/** One regulator-flag mini-report produced by the audit. */
export interface RegulatoryFinding {
  /** Short actionable title (also the inline marker label). */
  requirement: string;
  /** What is missing/wrong and why a regulator cares. */
  issue: string;
  /** Concrete steps to resolve it. */
  resolution: string;
  severity: string;
  status: string;
  anchor?: string;
}

/** Response from POST /runs/{runId}/sections/{moduleId}/audit. */
export interface RegulatoryAuditResult {
  module_id: string;
  status: "ok" | "no_guidance" | "llm_error";
  section_version: number;
  requirements: RegulatoryFinding[];
  evidence_summary: string;
  regulator_flag_count: number;
  /** The automatic gap fill the review started for the section's open
   *  missing-info / fill flags (proposals; narrated by ``gap_fill_*`` SSE). */
  gap_fill?: { started: boolean; issue_count: number };
}

/**
 * Run (or re-run) the regulatory audit on a section. Reads per-section
 * FDA/ICH guidance, complements with openFDA precedent, then inserts
 * ``[REGULATOR FLAG: …]`` recommendations into the draft. Idempotent —
 * re-running does not duplicate existing flags. Progress streams as
 * ``audit_*`` SSE events on the run's stream (drives the spine subline).
 *
 * module_id is interpolated raw (the backend route uses a ``:path``
 * converter) so CSR's slash-bearing IDs match — same as getSectionContent.
 */
export async function auditSection(
  runId: string,
  moduleId: string,
  drugContext?: { drug_name?: string; indication?: string; sponsor?: string },
): Promise<RegulatoryAuditResult> {
  return request("POST", `/runs/${runId}/sections/${moduleId}/audit`, drugContext ? { drug_context: drugContext } : {});
}

/**
 * Refresh a CUSTOM-N section's persisted ``draft_full`` from the live Word
 * document, immediately before a custom-run audit. A custom run's SSOT is only
 * written at import; the writer keeps editing in Word, so without this the audit
 * would annotate the stale import snapshot (and re-projecting it would revert
 * the writer's edits). ``content`` must be the section's CURRENT, table-aware
 * markdown (from ``readCustomSpineOutline``); the server rejects a blank body.
 * Only valid for custom submissions (server returns 409 otherwise). Best-effort
 * at the call site — a failure just leaves the audit on the last-synced content.
 */
export async function syncCustomSection(runId: string, moduleId: string, content: string): Promise<void> {
  await request("POST", `/runs/${runId}/sections/${moduleId}/custom-sync`, { content });
}

// ---------------------------------------------------------------------------
// Dynamic regulatory audit library (precedent-aware audit)
// ---------------------------------------------------------------------------

/** One source search issued during acquisition (audit trace). */
export interface AuditSearchAttemptDTO {
  provider: string;
  query: string;
  http_status: number | null;
  results: number;
  usable: number;
  note: string;
}

/** One candidate that was fetched but not ingested (audit trace). */
export interface AuditFilteredItemDTO {
  provider: string;
  title: string;
  url: string;
  reason: string;
  detail: string;
}

/** The run-spine AuditLibraryRef (DraftRunDoc.audit_library). */
export interface AuditLibraryRefDTO {
  id: string;
  name: string;
  status: "acquiring" | "processing" | "ready" | "empty" | "failed";
  documents_uploaded: number;
  created_at: string | null;
  updated_at: string | null;
  error: string | null;
  /** Coarse build-phase key while non-terminal ("analyzing"|"searching"|"uploading"). */
  stage?: string;
  /** Human phase text the panel shows instead of the static "Assembling…" string. */
  stage_detail?: string;
  /** Live determinate-progress counter for the current build stage, repurposed
   *  per stage (relevance-filtering: candidates checked / total; upload: docs
   *  added / total). Drives the panel's progress bar; 0/0 when idle. */
  progress_current?: number;
  progress_total?: number;
  /** User steer that produced this build (folded into search + relevance). */
  steering?: string;
  /** Set when this run adopted another run's audit library (same doc type + source library). */
  reused_from_run_id?: string | null;
  reused_from_run_name?: string;
  /** Acquisition trace — every search issued and every candidate dropped. */
  searches?: AuditSearchAttemptDTO[];
  filtered?: AuditFilteredItemDTO[];
  candidates_total?: number;
  candidates_relevant?: number;
  candidates_filtered?: number;
}

/** One document in the audit library, with its Sangnan parse state. */
export interface AuditLibraryDocDTO {
  id: string;
  name: string;
  parsed: boolean | null;
  status: string;
}

/** One acquired-source provenance row (spec §18) for "View source". */
export interface AuditEvidenceDTO {
  title: string;
  authority: string;
  source_type: string;
  evidence_class: string;
  source_url: string;
  tier: number;
  publication_date: string;
  filename: string;
  document_id: string | null;
}

/** Response from GET /runs/{runId}/audit-library. */
export interface RunAuditLibrary {
  enabled: boolean;
  audit_library: AuditLibraryRefDTO | null;
  /** The run's source library — the one this audit library was built for.
   *  The Library Hub offers the audit view only inside this library. */
  source_library_id?: string | null;
  documents: AuditLibraryDocDTO[];
  ready: boolean;
  total: number;
  processed: number;
  evidence: AuditEvidenceDTO[];
}

/**
 * Fetch the run's audit-library status, per-document processing, and acquired
 * evidence. Powers the library-view dropdown, the manual-audit readiness gate,
 * and the precedent "View source" list. Safe when the feature is off — returns
 * ``enabled:false`` with a null ``audit_library``.
 */
export async function getRunAuditLibrary(runId: string): Promise<RunAuditLibrary> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/audit-library`);
}

/**
 * Manually (re)build the run's dynamic audit library on the server. Optional
 * ``steering`` focuses the acquisition search terms + relevance judging. Each
 * build supersedes the prior one (fresh trace + cleared evidence). Returns
 * ``202`` immediately; poll {@link getRunAuditLibrary} for progress.
 */
export async function startRunAuditLibrary(
  runId: string,
  steering?: string,
): Promise<{ run_id: string; status: string; steering: string }> {
  const note = (steering ?? "").trim();
  return request("POST", `/runs/${encodeURIComponent(runId)}/audit-library`, note ? { steering: note } : undefined);
}

/** Response from POST /runs/{runId}/audit-library/documents. */
export interface AuditLibraryUploadResult {
  run_id: string;
  audit_library_id: string;
  uploaded: { filename: string; document_id: string }[];
  failed: string[];
}

/**
 * Add the writer's own files to the run's audit library. The library drops back
 * to ``processing`` until Sangnan parses them; poll {@link getRunAuditLibrary}.
 */
export async function addRunAuditLibraryDocuments(runId: string, files: File[]): Promise<AuditLibraryUploadResult> {
  const formData = new FormData();
  for (const file of files) formData.append("files", file);
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/audit-library/documents`, {
    method: "POST",
    credentials: "include",
    headers,
    body: formData,
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    const d = (detail as { detail?: unknown }).detail;
    const message =
      typeof d === "string"
        ? d
        : d && typeof d === "object" && "message" in d
          ? String((d as { message: unknown }).message)
          : `Upload failed: HTTP ${resp.status}`;
    throw new Error(message);
  }
  return resp.json();
}

/** Remove one document from the run's audit library. */
export async function deleteRunAuditLibraryDocument(
  runId: string,
  documentId: string,
): Promise<{ run_id: string; document_id: string; remaining: number; status: string }> {
  return request(
    "DELETE",
    `/runs/${encodeURIComponent(runId)}/audit-library/documents/${encodeURIComponent(documentId)}`,
  );
}

/**
 * True when an error thrown by {@link auditSection} is the audit-library
 * readiness 409 (the library is still processing). Callers use it to show the
 * per-doc status and keep "Run review" disabled until every doc is ready.
 */
export function isAuditLibraryNotReady(
  err: unknown,
): err is Error & { status: number; detail: { error: string; total?: number; processed?: number; status?: string } } {
  return (
    err instanceof Error &&
    (err as { status?: number }).status === 409 &&
    typeof (err as { detail?: unknown }).detail === "object" &&
    (err as { detail?: { error?: string } }).detail?.error === "audit_library_not_ready"
  );
}

/** One ``[REGULATOR FLAG: …]`` marker found in a section's draft. */
export interface RegulatorFlag {
  text: string;
  start: number;
  paragraph: number;
}

/** Response from GET /runs/{runId}/sections/{moduleId}/regulator-flags. */
export interface RegulatorFlagsResponse {
  module_id: string;
  section_version: number | null;
  status: string | null;
  regulator_flag_count: number;
  flags: RegulatorFlag[];
  /** Persisted mini-reports (requirement/issue/resolution/...) — present once
   *  the section has been audited; survives reload. */
  regulatory_findings: RegulatoryFinding[];
  regulatory_audited_at?: string | null;
  /** FDA precedent (openFDA) summary from the last audit — persisted so the
   *  "FDA precedent evidence" block re-renders on reopen. */
  regulatory_evidence_summary?: string;
  /** Live audit state from the server's Redis status key: "running" while an
   *  audit is in flight, "error" briefly after a failed one, null/undefined
   *  otherwise. Lets a remounted/reloaded panel attach to a running audit. */
  audit_status?: "running" | "error" | null;
  audit_started_at?: string | null;
  audit_error?: string | null;
}

/** List the regulator-flag recommendations currently in a section's draft
 *  (without re-running the audit). Used to populate the review tab on load. */
export async function getRegulatorFlags(runId: string, moduleId: string): Promise<RegulatorFlagsResponse> {
  return request("GET", `/runs/${runId}/sections/${moduleId}/regulator-flags`);
}

// ---------------------------------------------------------------------------
// Section issues (persistent gap/regulatory ledger)
// ---------------------------------------------------------------------------

/** How an issue left the ``open`` state. */
export interface SectionIssueResolution {
  method: "writer_manual" | "agent_edit" | "dismissed" | "superseded";
  actor?: string | null;
  conversation_id?: string | null;
  transaction_id?: string | null;
  edit_version?: number | null;
  note?: string | null;
}

/** One persistent gap/regulatory issue row from the section_issues ledger. */
export interface SectionIssue {
  id: string;
  module_id: string;
  /** ``directive_miss`` has no marker in the document: a drafting instruction
   *  the section did not cover, raised by the directive check. */
  kind: "data_gap" | "regulatory" | "language" | "directive_miss";
  flag_id: string;
  severity: string;
  /** Verbatim marker text incl. brackets — the needle for selectTextMarker. */
  marker_text: string;
  occurrence: number;
  status: "open" | "resolved" | "dismissed" | "stale";
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  resolved_at?: string | null;
  resolution?: SectionIssueResolution | null;
  source: string;
  /** For kind="regulatory": the finding's sub-type — guidance_expectation |
   *  precedent_signal | precedent_note | source_consistency. */
  category?: string | null;
  finding_anchor?: string | null;
  /** For kind="regulatory": the matched audit finding's "what is missing/wrong"
   *  explanation, joined server-side from ModuleRun.regulatory_findings. */
  finding_issue?: string | null;
  /** For kind="regulatory": the matched audit finding's concrete "how to
   *  resolve" steps — the same guidance the Review tab shows. */
  finding_resolution?: string | null;
  /** For kind="regulatory": the finding's verified citations — each a source
   *  title/authority, an optional locator (page/section), and a verbatim quote. */
  finding_evidence?: SectionIssueEvidence[] | null;
  /** The last "Fix with Asthra" attempt on this issue (overwritten each pass),
   *  so the row can say why a flag is still open after a reload. */
  last_fix?: SectionIssueFixAttempt | null;
}

/** Per-flag verdict of a resolve pass (``SectionIssueDoc.last_fix.outcome``):
 *  ``rewrite_rejected`` — the agent changed the text but the change didn't fill
 *  the flag; ``has_pending`` — skipped, a change for it already waits for review;
 *  ``busy`` — skipped, another pass was on the section; ``rejected`` — the
 *  writer rejected the proposed change. */
export type FixOutcome =
  | "proposed"
  | "not_in_sources"
  | "rewrite_rejected"
  | "not_placed"
  | "has_pending"
  | "busy"
  | "failed"
  | "stopped"
  | "rejected";

/** One issue's last fix attempt (``SectionIssueDoc.last_fix``). */
export interface SectionIssueFixAttempt {
  at?: string | null;
  outcome: FixOutcome;
  message?: string | null;
  proposal_id?: string | null;
  pass_id?: string | null;
}

/** One verified citation behind a regulatory finding (source + locator + quote). */
export interface SectionIssueEvidence {
  source_title?: string | null;
  authority?: string | null;
  document_ref?: string | null;
  locator?: string | null;
  quote?: string | null;
}

/** Response from GET /runs/{runId}/sections/{moduleId}/issues. */
export interface SectionIssuesResponse {
  module_id: string;
  issues: SectionIssue[];
  open_count: number;
}

/** List the persistent issues for one section. Lazily seeds the ledger from
 *  the persisted draft for runs that predate it. */
export async function getSectionIssues(
  runId: string,
  moduleId: string,
  status: "open" | "resolved" | "dismissed" | "stale" | "all" = "all",
): Promise<SectionIssuesResponse> {
  return request("GET", `/runs/${runId}/sections/${moduleId}/issues?status=${status}`);
}

/** Reconcile the section's issue ledger against the LIVE Word text. Markers
 *  missing from ``content`` are recorded as resolved-by-writer. */
export async function reconcileSectionIssues(
  runId: string,
  moduleId: string,
  content: string | null,
): Promise<{
  module_id: string;
  summary: Record<string, number>;
  issues: SectionIssue[];
}> {
  return request("POST", `/runs/${runId}/sections/${moduleId}/issues/reconcile`, content !== null ? { content } : {});
}

/** Dismiss ("Mark N/A") or reopen one issue — no document edit required. */
export async function patchSectionIssue(
  runId: string,
  issueId: string,
  status: "dismissed" | "open",
  note?: string,
): Promise<SectionIssue> {
  return request("PATCH", `/runs/${encodeURIComponent(runId)}/issues/${encodeURIComponent(issueId)}`, {
    status,
    ...(note ? { note } : {}),
  });
}

/** Per-status issue tallies for a run — always covers every status regardless
 *  of the ``status`` filter applied to the returned rows. */
export interface RunIssueStatusCounts {
  open: number;
  resolved: number;
  dismissed: number;
  stale: number;
  total: number;
}

/** One section's issues in the run-level rollup, with its display header. */
export interface RunIssueSection {
  module_id: string;
  /** Section number for the group header (e.g. "9.1"). */
  number: string;
  title: string;
  open_count: number;
  issues: SectionIssue[];
}

/** Response from GET /runs/{runId}/issues — the submission-wide issue rollup,
 *  grouped by section in submission order (``scoped_module_ids``). */
export interface RunIssuesResponse {
  run_id: string;
  status_counts: RunIssueStatusCounts;
  /** Sections in submission order; each carries its ordered issue rows. */
  sections: RunIssueSection[];
  /** Flat, submission-ordered issues (backward-compat mirror of ``sections``). */
  issues: SectionIssue[];
  /** Open-issue counts per module: {module_id: {data_gap, regulatory}}. */
  open_counts: Record<string, { data_gap: number; regulatory: number }>;
}

/** Submission-wide issue rollup across all sections of a run. Groups every
 *  gap/regulatory issue by section in submission order and lazily seeds
 *  pre-ledger modules. ``status`` filters the rows; ``status_counts`` always
 *  reflects every status. Powers the top-level Issues tab. */
export async function getRunIssues(
  runId: string,
  status: "open" | "resolved" | "dismissed" | "stale" | "all" = "all",
): Promise<RunIssuesResponse> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/issues?status=${status}`);
}

// ---------------------------------------------------------------------------
// Bulk "Resolve issues" (global Issues tab → resolve wizard → spine run)
// ---------------------------------------------------------------------------

/** One section's resolve request. Empty ``issue_ids`` = all its open issues. */
export interface ResolveIssueTarget {
  module_id: string;
  issue_ids: string[];
}

/** A reference document to scope resolve-time retrieval to. */
export interface ResolveIssueDoc {
  doc_id: string;
  name: string;
}

export interface ResolveIssuesRequestBody {
  targets: ResolveIssueTarget[];
  /** Restrict retrieval to these library docs' trees; empty = whole library. */
  document_scope: ResolveIssueDoc[];
  /** Redo flags whose change already waits for review (its card is replaced).
   *  Off by default: the pass reports them ``has_pending``. */
  force?: boolean;
}

export interface ResolveIssuesResponse {
  run_id: string;
  status: string;
  target_module_ids: string[];
  /** Stream tail id captured before dispatch — the add-in subscribes from this
   *  so it replays the resolve's events (module_start onward) with no tail race. */
  stream_cursor?: string | null;
}

/** Kick off the bulk resolve orchestrator. Reuses the run's SSE stream, so the
 *  resolution renders live on the spine. 202 + fire-and-forget. */
export async function resolveIssues(runId: string, body: ResolveIssuesRequestBody): Promise<ResolveIssuesResponse> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/resolve-issues`, body);
}

export type ResolveControlAction = "pause" | "cancel";

export interface ResolveControlResponse {
  run_id: string;
  status: string; // "pause_requested" | "cancel_requested"
  mode: string | null;
}

/** Pause or stop the resolve pass running on a run. Both stop at the next safe
 *  point (between paragraphs / sections; an in-flight rewrite is cut at its next
 *  tool boundary and its section left untouched). ``pause`` makes the pass report
 *  its remaining targets on ``resolve_stopped`` so the client can resume by
 *  re-posting them. 404 when no pass is active. */
export async function controlResolveIssues(
  runId: string,
  action: ResolveControlAction,
): Promise<ResolveControlResponse> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/resolve-issues/control`, { action });
}

export interface ResolveIssuesStatus {
  run_id: string;
  active: boolean;
  progress: { done: number; total: number } | null;
  stream_cursor: string | null;
  /** "pause" | "cancel" once a stop was asked for. */
  mode: string | null;
  /** What the running pass is fixing (absent on an older server). */
  targets?: { module_id: string; issue_ids: string[] }[];
}

/** Whether a resolve pass is running on a run — lets a reloaded add-in
 *  re-attach to it, and settles a Pause/Stop whose request failed. */
export async function getResolveIssuesStatus(runId: string): Promise<ResolveIssuesStatus> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/resolve-issues`);
}

/** One flag's verdict in a past pass. */
export interface FixPassFlag {
  issue_id: string;
  module_id: string;
  category: string;
  marker_text: string;
  outcome: FixOutcome;
  message: string;
  proposal_id?: string | null;
}

/** One finished or stopped "Fix with Asthra" pass, as the history lists it. */
export interface FixPass {
  pass_id: string;
  run_id: string;
  started_at: string | null;
  finished_at: string | null;
  /** "finished" | "paused" | "stopped" */
  ended: string;
  requested_by: string | null;
  /** Document names searched; empty = the whole library. */
  document_scope: string[];
  summary: Record<FixOutcome | "proposals" | "sections", number>;
  flags: FixPassFlag[];
}

/** The run's fix passes, newest first. */
export async function getResolveHistory(runId: string): Promise<{ run_id: string; passes: FixPass[] }> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/resolve-issues/history`);
}

// ---------------------------------------------------------------------------
// Resolve proposals — the review queue (Approve / Reject / Revise)
// ---------------------------------------------------------------------------

export type ProposalStatus = "pending" | "approved" | "rejected" | "superseded";

/** One rewritten row of a table proposal. */
export interface SectionProposalTableRowEdit {
  /** 0-based Word ``getCell`` row index (header = 0). */
  row_index: number;
  /** The row before the rewrite, as a markdown pipe row. */
  original_row: string;
  /** The proposed row, as a markdown pipe row. */
  proposed_row: string;
}

/** One pending (or decided) per-paragraph rewrite produced by a resolve pass. */
export interface SectionProposal {
  id: string;
  module_id: string;
  /** The exact enclosing paragraph/block before the rewrite (diff "before"). */
  original_text: string;
  /** The proposed rewrite (diff "after"). */
  proposed_text: string;
  /** Ledger issue ids this rewrite fills. */
  issue_ids: string[];
  /** Verbatim marker text(s) — the needle for scrolling to / anchoring the span. */
  marker_texts: string[];
  /** True when the span is a GFM table rewritten one row at a time. When set,
   *  ``table_row_edits`` carries the per-row before/after and approve applies by
   *  row index (both the SSOT server-side and the Word mirror). */
  is_table?: boolean;
  /** Per-row edits for a table proposal: ``row_index`` is the Word ``getCell``
   *  index (header = 0); ``original_row``/``proposed_row`` are markdown pipe rows. */
  table_row_edits?: SectionProposalTableRowEdit[];
  /** ``title_field``: a title-page field fill — ``original_text`` /
   *  ``proposed_text`` are the field's old and new VALUE, and approve returns
   *  the merged ``title_fields``. Absent on older rows (= ``text``). */
  target?: "text" | "title_field";
  /** The title-page field key a ``title_field`` proposal fills. */
  title_field_key?: string | null;
  /** Target issues this change does NOT close (still flagged after it). */
  unfixed_issue_ids?: string[];
  /** Where a change not made by the model came from, in words — e.g.
   *  "Copied from CER 2025.docx, section 2.6 Device description". */
  source_note?: string | null;
  status: ProposalStatus;
  /** The section_version the rewrite was computed against (approve OCC guard). */
  computed_section_version: number;
  revision_count: number;
  supersedes_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface RunProposalSection {
  module_id: string;
  number: string;
  title: string;
  pending_count: number;
  proposals: SectionProposal[];
}

export interface RunProposalStatusCounts {
  pending: number;
  approved: number;
  rejected: number;
  superseded: number;
  total: number;
}

export interface RunProposalsResponse {
  run_id: string;
  status_counts: RunProposalStatusCounts;
  /** Sections in submission order; each carries its ordered proposals. */
  sections: RunProposalSection[];
}

/** The review queue: pending resolve proposals grouped by section in submission
 *  order. ``status`` filters the rows; ``status_counts`` always reflects every
 *  status. */
export async function getRunProposals(
  runId: string,
  status: ProposalStatus | "all" = "pending",
): Promise<RunProposalsResponse> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/proposals?status=${status}`);
}

export interface DecideProposalResponse {
  proposal: SectionProposal;
  /** Present on approve — the section's new version after applying the paragraph. */
  section_version?: number;
  filled?: number;
  remaining?: number;
  /** Present on approve — the section's full draft after the paragraph landed. */
  new_draft?: string | null;
  /** Present on approve of a ``title_field`` proposal — the merged title fields. */
  title_fields?: Record<string, string>;
}

/** Approve (apply to the section SSOT + reconcile the ledger) or reject (leave
 *  the issue open) one pending proposal. On approve the client mirrors the one
 *  approved paragraph into the open Word document. A ``409 span_drift`` means the
 *  paragraph changed since the rewrite was prepared — refresh the queue. */
export interface UndoProposalResponse {
  proposal: SectionProposal;
  section_version: number;
  /** The text the approval put in the section (now swapped back out). */
  applied_text: string;
  original_text: string;
}

/** Undo an approved paragraph change: the server puts the original paragraph
 *  back, reopens the flags it filled and returns the proposal to the review
 *  queue. Text proposals only; 409 ``span_drift`` when the paragraph changed
 *  since, ``undo_expired`` when too old. The caller mirrors the swap into Word. */
export async function undoProposal(runId: string, proposalId: string): Promise<UndoProposalResponse> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(runId)}/proposals/${encodeURIComponent(proposalId)}/undo`,
  );
}

export async function decideProposal(
  runId: string,
  proposalId: string,
  action: "approve" | "reject",
  /** The writer's own edit of the proposed text (approve, text proposals only). */
  content?: string,
): Promise<DecideProposalResponse> {
  return request(
    "PATCH",
    `/runs/${encodeURIComponent(runId)}/proposals/${encodeURIComponent(proposalId)}`,
    content === undefined ? { action } : { action, content },
  );
}

export interface SourceOutline {
  /** ``word``: read from the file itself (exact); ``extracted``: the library's
   *  extraction of a PDF or other file. */
  format: "word" | "extracted";
  sections: { number: string; title: string; level: number }[];
  tables: { index: number | null; caption: string; section: string }[];
}

/** The sections and tables of one source document, for "Copy from a source". */
export async function getSourceOutline(runId: string, docId: string): Promise<SourceOutline> {
  return request(
    "GET",
    `/runs/${encodeURIComponent(runId)}/sources/${encodeURIComponent(docId)}/outline`,
  );
}

export interface CopyFromSourceResponse {
  /** The pending proposal, or null when the source text equals the draft
   *  (or for ``replace_text``, which returns ``preview`` instead). */
  proposal: SectionProposal | null;
  /** ``replace_text`` (and a section never drafted): the copy, ready for the
   *  highlight-edit review / a Word-only replace. */
  preview?: SelectionEditResponse & {
    /** A whole-section copy from a Word source: the section as a Word file. */
    source_docx?: string | null;
  };
  /** ``replace_section`` on a drafted section: the copy parked as a "redraft"
   *  proposal — the same pipeline as Regenerate (offer it to the ad-hoc flow). */
  redraft?: {
    module_id: string;
    content: string;
    original_text: string;
    word_count: number;
    base_section_version: number;
    /** From a Word source: the section cut out of the source file (base64
     *  .docx), which Word inserts as it is, formatting included. */
    source_docx?: string | null;
  };
  unchanged?: boolean;
  source_note: string;
  tables?: number;
  figures_skipped?: number;
}

export interface CopyFromSourceBody {
  doc_id: string;
  doc_name: string;
  kind?: "section" | "table";
  source_number?: string;
  source_title?: string;
  table_index?: number | null;
  table_caption?: string;
  placement?: "replace_section" | "after_text" | "replace_text";
  anchor_text?: string;
}

/** Propose a section or table copied word for word from a source document
 *  (no model). The result waits in the review queue like any other change. */
export async function copySectionFromSource(
  runId: string,
  moduleId: string,
  body: CopyFromSourceBody,
): Promise<CopyFromSourceResponse> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/copy-from-source`,
    body,
  );
}

export interface ProposalReviseRequestBody {
  guidance: string;
  library_id?: string | null;
  document_scope?: ResolveIssueDoc[];
}

/** Streaming rewrite of a proposed paragraph under reviewer guidance — POST
 *  /runs/{runId}/proposals/{id}/revise. Emits the same SSE progress events the
 *  selection-edit stream does (plan_ready / task_start / retrieval_step /
 *  drafting_response), then a terminal ``complete`` carrying the NEW proposal
 *  (``proposal_id`` / ``proposed_text``) that supersedes this one. The old
 *  proposal keeps BOTH its original and current proposed text in context.
 *  Never resolves a value — the caller assembles from ``onEvent``. */
export async function reviseProposalStream(
  runId: string,
  proposalId: string,
  body: ProposalReviseRequestBody,
  onEvent: (event: Record<string, unknown>) => void,
): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getSessionToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(
    `${API_BASE}/runs/${encodeURIComponent(runId)}/proposals/${encodeURIComponent(proposalId)}/revise`,
    {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    },
  );

  if (!resp.ok || !resp.body) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (line.startsWith("data: ")) {
        try {
          onEvent(JSON.parse(line.slice(6)));
        } catch {
          // Skip malformed events
        }
      }
    }
  }
}

/** One persona-review issue (flattened across personas). Mirrors the
 *  review_agent ``PersonaIssue`` shape persisted on ``ReviewVerdictDoc``. */
export interface ReviewIssue {
  dimension?: string;
  severity?: string;
  description?: string;
  rubric_ref?: string;
  ich_e3_section?: string | null;
  draft_excerpt?: string;
  suggestion?: string;
  persona_id?: string;
}

/** Per-persona issue breakdown for a section. */
export interface ReviewPersona {
  persona_id: string;
  verdict: string;
  status: string;
  counts: { critical: number; major: number; minor: number; info: number };
  issues: ReviewIssue[];
}

/** Response from GET /runs/{runId}/sections/{moduleId}/review-issues — the
 *  persisted persona-review verdict, readable without the live sidecar. */
export interface ReviewIssuesResponse {
  module_id: string;
  has_review: boolean;
  aggregated_verdict: string | null;
  counts: { critical: number; major: number; minor: number; info: number };
  flagged_count: number;
  issues: ReviewIssue[];
  personas: ReviewPersona[];
  reviewed_at?: string | null;
}

/** Load the persisted persona-review issues for a section (sidecar-independent).
 *  Returns ``has_review: false`` when no verdict has been written yet. */
export async function getReviewIssues(runId: string, moduleId: string): Promise<ReviewIssuesResponse> {
  return request("GET", `/runs/${runId}/sections/${moduleId}/review-issues`);
}

export async function pauseRun(runId: string): Promise<void> {
  await request("POST", `/runs/${runId}/pause`);
}

/** "Draft now": the sections drafting right now stop retrieving and draft
 *  from what they have gathered; no new section starts and the run then
 *  pauses (Resume drafts the rest). A single-section regen just finishes
 *  early. */
export async function draftNowRun(runId: string): Promise<void> {
  await request("POST", `/runs/${encodeURIComponent(runId)}/draft-now`);
}

export async function resumeRun(runId: string): Promise<{
  run_id: string;
  attempt_id?: string | null;
  status: string;
  resume_count: number;
}> {
  return request("POST", `/runs/${runId}/resume`);
}

/** Create a retrieval plan (uses Claude Opus). Returns the plan for writer approval. */
export async function createPlan(params: StartRunParams): Promise<{
  plan_enabled: boolean;
  api_healthy: boolean;
  plan?: Record<string, unknown>;
  plan_id?: string;
  plan_summary?: string;
  error?: string;
}> {
  return request("POST", "/plan", params);
}

/**
 * Fetch all section content from a completed run.
 * Used by batch document population — gets everything in one call.
 */
export async function getAllSectionContent(runId: string): Promise<
  Array<{
    section_number: string;
    section_title: string;
    draft_content: string;
    module_id: string;
    /** The version this body is at — the guard a save presents back.
     *
     *  Optional because a server older than this route's change does not send
     *  one, and a save that cannot say which version it read must send no guard
     *  rather than guess at zero: zero is a real version, and presenting it
     *  would refuse every section that has ever been edited. */
    section_version?: number;
    title_fields?: Record<string, string>;
    /** Persisted per-section token telemetry. 0/None on older runs. */
    input_tokens?: number;
    output_tokens?: number;
    draft_model?: string | null;
  }>
> {
  return request("GET", `/runs/${runId}/all-sections`);
}

// ---------------------------------------------------------------------------
// Batch-SSOT — runs index, run bundle download, section state, write-back.
// Backs the DraftsPanel in variant-c.
// ---------------------------------------------------------------------------

/** Lifecycle status for one section in the run's spine.
 *
 *  Mirrors ``server.section_state.LifecycleStatus`` — kept in sync by
 *  the existing Mongo validator + Pydantic field on ``ModuleRun``.
 *
 *  Legacy ``needs_approval`` is coerced to ``drafted`` server-side, so
 *  this union deliberately omits it.
 */
export type SectionLifecycleStatus = "queued" | "drafting" | "drafted" | "verified" | "inserted" | "edited" | "flagged";

/** Undecided ad-hoc draft proposal surfaced by ``sections/state`` so a
 *  reloaded pane can re-offer the Approve/Reject card. Absent/null once
 *  decided or replaced. */
export interface PendingAdhocProposal {
  instruction: string;
  word_count: number;
  created_at: string | null;
  base_section_version: number | null;
  content: string;
  /** "redraft" = full-section redraft proposal (/regen command);
   *  absent/"draft" = ad-hoc draft fragment inserted at ``anchor_text``. */
  mode?: "draft" | "redraft";
  /** ``draft`` mode: the paragraph the fragment is inserted after. */
  anchor_text?: string | null;
}

export interface SectionStateSnapshot {
  module_id: string;
  gate: string;
  status: SectionLifecycleStatus;
  spine_status_updated_at: string | null;
  verified_at: string | null;
  inserted_at: string | null;
  last_edited_at: string | null;
  last_editor: "agent" | "user" | "agent_redraft" | null;
  section_version: number;
  module_error: boolean;
  content_gap_count?: number | null;
  pending_adhoc_proposal?: PendingAdhocProposal | null;
}

export interface RunBundleSummary {
  run_id: string;
  doc_id: string;
  workspace_id: string | null;
  library_id: string | null;
  library_name: string | null;
  doc_type: string;
  project_name: string;
  /** User-chosen draft name; preferred over `title` for display when set. */
  name?: string | null;
  title: string | null;
  status: "completed" | "failed" | "cancelled";
  completed_at: string; // ISO 8601
  /** Run creation timestamp (ISO 8601). Absent on legacy rows. */
  created_at?: string | null;
  modules_completed: number;
  /** Sections the writer selected to draft for this run (the run's scope). */
  modules_total: number;
  /**
   * Total sections in the whole submission (every module the doc type
   * declares) — the progress-bar denominator, so a partial run reads as
   * progress against the whole document, not just its selected scope. May be
   * 0 (legacy rows / unresolved doc type); clients fall back to `modules_total`.
   */
  sections_total?: number;
  /** Total tokens across the run. 0 on legacy rows. */
  total_input_tokens?: number;
  total_output_tokens?: number;
  /** The folder this document is filed in (joined from the session), or null
   *  when unfiled. Filing is per-document, so this is the same value on every
   *  run of one document. */
  folder_id?: string | null;
}

export interface RunsIndexResponse {
  scope: "doc_id" | "workspace_id" | "library_id" | "user";
  count: number;
  documents: Array<{ doc_id: string; runs: RunBundleSummary[]; folder_id?: string | null }>;
}

/** List run bundles, grouped by doc_id. Scoping precedence:
 *  doc_id > workspace_id > library_id. */
export async function listRuns(params: {
  docId?: string;
  workspaceId?: string;
  libraryId?: string;
  limit?: number;
}): Promise<RunsIndexResponse> {
  const qp = new URLSearchParams();
  if (params.docId) qp.set("doc_id", params.docId);
  if (params.workspaceId) qp.set("workspace_id", params.workspaceId);
  if (params.libraryId) qp.set("library_id", params.libraryId);
  if (params.limit) qp.set("limit", String(params.limit));
  return request("GET", `/runs/index?${qp.toString()}`);
}

/** List all completed run bundles created by the authenticated user.
 *
 *  ``workspaceId`` (optional) scopes the list to one workspace so the drafts
 *  panel shows the user's runs in the current workspace only. */
export async function listUserRuns(
  limit = 100,
  workspaceId?: string | null,
  folderId?: string | null,
): Promise<RunsIndexResponse> {
  const qp = new URLSearchParams();
  qp.set("limit", String(limit));
  if (workspaceId) qp.set("workspace_id", workspaceId);
  if (folderId) qp.set("folder_id", folderId);
  return request("GET", `/runs/user/index?${qp.toString()}`);
}

/** The documents filed in each folder across ALL the user's completed runs
 *  (not one page) — what Home's folder cards count from. */
export async function listUserFolderDocuments(
  workspaceId?: string | null,
): Promise<{ folders: Record<string, string[]> }> {
  const qp = new URLSearchParams();
  if (workspaceId) qp.set("workspace_id", workspaceId);
  return request("GET", `/runs/user/folder-documents?${qp.toString()}`);
}

/** Summary of an in-progress run, returned by ``GET /runs/active``.
 *
 *  Companion shape to ``RunBundleSummary`` so the DraftsPanel can render
 *  live and completed rows in a single merged list. ``live: true`` is
 *  the discriminator the UI keys on to swap actions (Join live vs. Load).
 */
export interface ActiveRunSummary {
  run_id: string;
  doc_id: string;
  doc_type: string;
  library_id: string | null;
  library_name: string | null;
  project_name: string;
  /** User-chosen draft name; preferred over `title` for display when set. */
  name?: string | null;
  title: string | null;
  status: "pending" | "running" | "paused";
  started_at: string | null;
  /** Run creation timestamp (ISO 8601). Absent on legacy rows. */
  created_at?: string | null;
  modules_completed: number;
  /** Sections the writer selected to draft for this run (the run's scope). */
  modules_total: number;
  /** Total sections in the whole submission. See `RunBundleSummary.sections_total`. */
  sections_total?: number;
  /** Total tokens accumulated so far. 0 on legacy rows. */
  total_input_tokens?: number;
  total_output_tokens?: number;
  /** The folder this document is filed in, or null when unfiled. */
  folder_id?: string | null;
  live: true;
}

export interface ActiveRunsResponse {
  scope: "doc_id" | "workspace_id" | "library_id" | "user";
  count: number;
  runs: ActiveRunSummary[];
}

/** List in-progress runs (status pending/running/paused) for the given
 *  scope. Complements ``listRuns`` which returns only finished bundles.
 *  Used by the DraftsPanel and HomeScene to surface a "Live" row that the
 *  writer can Join into a fresh add-in session and continue streaming.
 *
 *  Scope precedence server-side is ``doc_id > workspace_id > library_id``
 *  (first non-empty wins). ``workspace_id`` returns every in-progress run in
 *  the workspace CROSS-USER (backed by the ``draft_runs_workspace_created_at``
 *  index) — use it for the workspace-wide live feed; ``doc_id`` narrows to a
 *  single document's live run.
 */
export async function listActiveRuns(params: {
  docId?: string;
  workspaceId?: string;
  libraryId?: string;
  limit?: number;
}): Promise<ActiveRunsResponse> {
  const qp = new URLSearchParams();
  if (params.docId) qp.set("doc_id", params.docId);
  if (params.workspaceId) qp.set("workspace_id", params.workspaceId);
  if (params.libraryId) qp.set("library_id", params.libraryId);
  if (params.limit) qp.set("limit", String(params.limit));
  return request("GET", `/runs/active?${qp.toString()}`);
}

/** List in-progress runs created by the authenticated user. */
export async function listUserActiveRuns(limit = 50): Promise<ActiveRunsResponse> {
  const qp = new URLSearchParams();
  qp.set("limit", String(limit));
  return request("GET", `/runs/user/active?${qp.toString()}`);
}

/** Resolve the run id whose draft should be projected into the live Word doc
 *  on session refresh.
 *
 *  Strategy:
 *    1. List in-progress runs for ``docId`` via ``listActiveRuns``. If any
 *       running/paused row exists, return the most recent — it should win
 *       so the writer immediately re-attaches to the live stream after a
 *       refresh.
 *    2. Otherwise list completed runs via ``listRuns`` (``RunBundleDoc``
 *       index, sorted by ``completed_at`` desc server-side).
 *    3. If ``activeRunId`` is provided and is NOT in either list, treat it
 *       as a newer in-progress run and return it.
 *    4. Otherwise return the most-recent completed ``run_id``, falling back
 *       to ``activeRunId`` if both lists are empty.
 *
 *  Returns ``null`` if no run can be found at all. Network/server errors
 *  fall back to ``activeRunId`` so a transient failure doesn't block restore.
 */
export async function resolveLatestRunIdForDoc(docId: string, activeRunId?: string | null): Promise<string | null> {
  // Live runs take priority — a fresh add-in session should re-join the
  // run that's currently streaming, not load the most recent completed
  // bundle from yesterday.
  try {
    const liveResp = await listActiveRuns({ docId, limit: 10 });
    if (liveResp.runs.length > 0) {
      const sorted = [...liveResp.runs].sort((a, b) => {
        const aTs = a.started_at ?? "";
        const bTs = b.started_at ?? "";
        return bTs.localeCompare(aTs);
      });
      return sorted[0].run_id;
    }
  } catch (err) {
    console.warn("[resolveLatestRunIdForDoc] listActiveRuns failed; falling through:", err);
  }

  try {
    const resp = await listRuns({ docId, limit: 50 });
    const completed = resp.documents.flatMap((g) => g.runs);
    if (activeRunId && !completed.some((r) => r.run_id === activeRunId)) {
      return activeRunId;
    }
    if (completed.length === 0) return activeRunId ?? null;
    const latest = [...completed].sort((a, b) => b.completed_at.localeCompare(a.completed_at))[0];
    return latest.run_id;
  } catch (err) {
    console.warn("[resolveLatestRunIdForDoc] listRuns failed, falling back to activeRunId:", err);
    return activeRunId ?? null;
  }
}

/** Stream a run bundle as a zip Blob — triggers a browser download. */
export async function fetchRunBundle(runId: string, options?: { includeProvenance?: boolean }): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const includeProvenance = options?.includeProvenance ?? true;
  const query = includeProvenance ? "" : "?include_provenance=false";

  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/bundle${query}`, {
    method: "GET",
    credentials: "include",
    headers,
  });

  if (!resp.ok) {
    throw new Error(`fetchRunBundle: HTTP ${resp.status} ${resp.statusText}`);
  }
  return resp.blob();
}

/** Parse the ``filename`` out of a ``Content-Disposition`` header.
 *
 *  Prefers RFC 5987 ``filename*=UTF-8''…`` (percent-decoded) when present, else
 *  the plain ``filename="…"`` token. Returns ``null`` when the header is absent
 *  or carries no filename so callers can fall back to a synthesised name. */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (extended?.[1]) {
    const raw = extended[1].trim().replace(/^["']|["']$/g, "");
    try {
      return decodeURIComponent(raw) || null;
    } catch {
      return raw || null;
    }
  }
  const basic = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return basic?.[1]?.trim() || null;
}

/** Download the original stored file for a document (source library or audit
 *  library share the same webapi ``Document.id``). Returns the bytes plus the
 *  server-chosen filename read from ``Content-Disposition`` — never
 *  reconstructed from the display name. ``page`` (1-based) fetches a single PDF
 *  page; omit for the whole file. Throws with the upstream detail on failure. */
export async function fetchDocumentDownload(docId: string, page?: number): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const qs = page != null ? `?page=${encodeURIComponent(String(page))}` : "";
  const resp = await fetch(`${API_BASE}/documents/${encodeURIComponent(docId)}/download${qs}`, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => null);
    const message =
      (detail && typeof detail === "object" && "detail" in detail
        ? String((detail as { detail: unknown }).detail)
        : null) ?? `Download failed: HTTP ${resp.status}`;
    throw new Error(message);
  }
  const filename = parseContentDispositionFilename(resp.headers.get("content-disposition")) ?? docId;
  return { blob: await resp.blob(), filename };
}

// ---------------------------------------------------------------------------
// EndNote reference package (GET /runs/{runId}/references[.ris])
// ---------------------------------------------------------------------------

/** One literature source a run's drafters used or were shown. ``placeholder``
 *  is the server-rendered EndNote temporary citation ({Surname, Year, title
 *  words}) — insert it verbatim, never compose one client-side. */
export interface RunReference {
  citation_key: string;
  title: string;
  authors: string[];
  year: number | null;
  journal: string | null;
  volume: string | null;
  issue: string | null;
  pages: string | null;
  doi: string | null;
  pmid: string | null;
  nct_id: string | null;
  url: string | null;
  abstract: string;
  source: string;
  /** Section numbers whose CURRENT text cites it (empty = found, not cited). */
  cited_in_sections: string[];
  placeholder: string;
  /** Display-only one-liner (EndNote owns real formatting). */
  formatted: string;
}

export interface RunReferencesResponse {
  run_id: string;
  doc_type: string;
  count: number;
  cited_count: number;
  references: RunReference[];
}

export async function getRunReferences(runId: string, moduleId?: string): Promise<RunReferencesResponse> {
  const qs = moduleId ? `?module_id=${encodeURIComponent(moduleId)}` : "";
  return request<RunReferencesResponse>("GET", `/runs/${encodeURIComponent(runId)}/references${qs}`);
}

/** The run's references as a ``.ris`` file for EndNote / Zotero import. */
export async function fetchRunReferencesRis(
  runId: string,
  opts: { citedOnly?: boolean } = {},
): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const qs = opts.citedOnly ? "?cited_only=true" : "";
  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/references.ris${qs}`, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => null);
    const message =
      (detail && typeof detail === "object" && "detail" in detail
        ? String((detail as { detail: unknown }).detail)
        : null) ?? `Download failed: HTTP ${resp.status}`;
    throw new Error(message);
  }
  const filename = parseContentDispositionFilename(resp.headers.get("content-disposition")) ?? "references.ris";
  return { blob: await resp.blob(), filename };
}

/** The writer's Word document (``docx``, as it stands in Word) back without
 *  its regulator, language and reviewer notes — a copy to share. */
export async function fetchCleanCopy(runId: string, docx: Blob): Promise<Blob> {
  const headers: Record<string, string> = {
    "Content-Type": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/clean-copy`, {
    method: "POST",
    credentials: "include",
    headers,
    body: docx,
  });
  if (!resp.ok) throw new Error(`Clean copy failed: HTTP ${resp.status}`);
  return resp.blob();
}

/** Trigger a browser download for a Blob via a transient object URL + anchor
 *  click. The one shared copy for new call sites (the older duplicates in
 *  DraftsPanel/HomeScene/export.ts predate this and are left as-is). */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined" || typeof URL === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the download a tick to start before revoking — some browsers race the
  // revoke against the download initiation.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Fetch the server-rendered, standalone .docx for a run as a Blob.
 *
 *  Returns ``null`` when the run has no bundle yet (HTTP 404) — this is the
 *  expected case for a still-running/paused run whose document hasn't been
 *  exported, and lets the restore path fall back to the per-section JSON
 *  hydration. Any other non-OK status throws.
 */
export async function fetchRunDocument(
  runId: string,
  options?: { includeProvenance?: boolean; preferRebuild?: boolean },
): Promise<Blob | null> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const includeProvenance = options?.includeProvenance ?? true;
  // ``preferRebuild`` makes the server serve the deterministic Mongo/on-disk
  // rebuild and never the DocService live mirror — Load callers set it so a
  // run loads identically every time (and a DocService fault can't 502 a Load).
  const params = new URLSearchParams();
  if (!includeProvenance) params.set("include_provenance", "false");
  if (options?.preferRebuild) params.set("prefer_rebuild", "true");
  const query = params.toString() ? `?${params.toString()}` : "";

  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/document${query}`, {
    method: "GET",
    credentials: "include",
    headers,
  });

  if (resp.status === 404) return null;
  if (!resp.ok) {
    throw new Error(`fetchRunDocument: HTTP ${resp.status} ${resp.statusText}`);
  }
  return resp.blob();
}

/** Encode a Blob as a base64 string (no data-URI prefix), suitable for
 *  Office.js ``Body.insertFileFromBase64``. Chunked ``String.fromCharCode``
 *  avoids a call-stack overflow on large (multi-MB) documents and is faster
 *  than ``FileReader.readAsDataURL`` (which would also prepend a prefix we'd
 *  have to strip). */
export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000; // 32 KiB per chunk
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Cheap sanity check that a base64 payload is a real .docx before handing it
 *  to Office.js ``Body.insertFileFromBase64`` — which throws an opaque
 *  ``ItemNotFound`` / ``GeneralException`` when fed bytes that aren't a valid
 *  OOXML (ZIP) package. A .docx is a ZIP, so the decoded bytes must begin with
 *  the local-file-header signature ``PK\x03\x04`` ("UEsDB" once base64-encoded)
 *  and carry more than a trivial header's worth of data. Guards against the
 *  case where ``/document`` returns HTTP 200 with a non-docx body (HTML error
 *  page, JSON, or a 0-byte file). */
export function isLikelyDocx(base64Docx: string): boolean {
  if (typeof base64Docx !== "string" || base64Docx.length < 64) return false;
  // "PK\x03\x04" base64-encodes to "UEsDBA" (the 4-byte ZIP signature spans
  // the first 6 base64 chars). Compare the leading bytes rather than the
  // string so whitespace/newlines in the payload don't trip the check.
  return base64Docx.startsWith("UEsDB");
}

/** Get the per-section lifecycle snapshot for a run (the spine view). */
export async function getSectionState(runId: string): Promise<{
  run_id: string;
  doc_id: string;
  status: string;
  sections: SectionStateSnapshot[];
}> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/sections/state`);
}

/** Submit an edit to a section. ``source`` discriminates manual user
 *  edits from agent re-drafts. Optimistic concurrency: pass the
 *  ``section_version`` last seen — a 409 response means a parallel
 *  write landed first; the response body carries the current state to
 *  resolve against. */
export async function submitSectionEdit(params: {
  runId: string;
  moduleId: string;
  content: string;
  source: "user" | "agent_redraft";
  expectedVersion?: number;
  /** Optional formatting snapshot (raw flat-OPC package + normalized hash,
   *  both-or-neither) — upserted server-side at the post-bump version. */
  ooxml?: string;
  ooxmlHash?: string;
  /** Section number, for provenance rows written alongside the edit. */
  sectionNumber?: string | null;
  /** Retrieval provenance from a chat edit-highlight rewrite — appended to
   *  the section's provenance server-side, only now that the edit applies. */
  transactions?: RetrievalProvenanceItem[];
  /** The rewrite agent's tool-call log — lands on Action History with the edit. */
  toolCalls?: Record<string, unknown>[];
  /** How this edit came about; recorded as ``section.edit_applied`` so Action
   *  History can say "Approved the suggested rewrite" vs "Manual save". */
  editKind?: EditKind;
  /** Root agent span of the rewrite (groups its tool calls under the approval). */
  rootSpanId?: string | null;
  /** Chat edit-highlight TRIGGER — the user's instruction + the highlighted
   *  text. Persisted on ``section.edit_applied`` so Action History heads the
   *  edit's subtree with what the user asked for. */
  instruction?: string | null;
  selectedText?: string | null;
}): Promise<{
  module_id: string;
  from: SectionLifecycleStatus;
  to: SectionLifecycleStatus;
  section_version: number;
  at: string;
}> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(params.runId)}/sections/${encodeURIComponent(params.moduleId)}/edit`,
    {
      content: params.content,
      source: params.source,
      expected_version: params.expectedVersion,
      ...(params.sectionNumber ? { section_number: params.sectionNumber } : {}),
      ...(params.transactions?.length ? { transactions: params.transactions } : {}),
      ...(params.toolCalls?.length ? { tool_calls: params.toolCalls } : {}),
      ...(params.editKind ? { edit_kind: params.editKind } : {}),
      ...(params.rootSpanId ? { root_span_id: params.rootSpanId } : {}),
      ...(params.instruction ? { instruction: params.instruction } : {}),
      ...(params.selectedText ? { selected_text: params.selectedText } : {}),
      ...(params.ooxml && params.ooxmlHash ? { ooxml: params.ooxml, ooxml_hash: params.ooxmlHash } : {}),
    },
  );
}

/** How a section edit came about — the server records it on ``section.edit_applied``. */
export type EditKind = "approved_proposal" | "manual_save" | "bulk_save";

/** Store a picture the writer inserted by hand into a section, under its
 *  figure title. The section's markdown then points at it as
 *  ``[IMAGE DATA: <caption>]`` and every Load / download inserts it on the spot
 *  (``POST /runs/{id}/sections/{module}/figures``). Throws on failure — the
 *  caller must then NOT save the section, or the figure would be lost. */
export async function uploadSectionFigure(params: {
  runId: string;
  moduleId: string;
  caption: string;
  image: Blob;
}): Promise<{ asset_id: string; byte_size: number; media_type: string }> {
  const headers: Record<string, string> = {
    "Content-Type": params.image.type || "image/png",
    "X-Asthra-Caption": encodeURIComponent(params.caption),
  };
  const token = getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(
    `${API_BASE}/runs/${encodeURIComponent(params.runId)}/sections/${encodeURIComponent(params.moduleId)}/figures`,
    { method: "POST", credentials: "include", headers, body: params.image },
  );
  if (!resp.ok) {
    const detail = await resp.text().catch(() => "");
    throw new Error(`figure upload failed (${resp.status}) ${detail.slice(0, 200)}`);
  }
  return resp.json();
}

/** URL of the writer-inserted figure titled ``caption`` in a run's document
 *  (404 when none — then resolve the caption from the source library). */
export function sectionFigureUrl(runId: string, caption: string): string {
  return `${API_BASE}/runs/${encodeURIComponent(runId)}/figures?caption=${encodeURIComponent(caption)}`;
}

/** Formatting-only save: upsert a section's OOXML snapshot WITHOUT touching
 *  the markdown SSOT (no version bump / draft_full / edits[] / SSE).
 *  ``expectedSectionVersion`` is mandatory — 409 means a concurrent write
 *  landed between capture and submit; treat as "skip, recapture next Save". */
export async function submitSectionOoxml(params: {
  runId: string;
  moduleId: string;
  ooxml: string;
  ooxmlHash: string;
  expectedSectionVersion: number;
}): Promise<{ module_id: string; section_version: number; ooxml_hash: string }> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(params.runId)}/sections/${encodeURIComponent(params.moduleId)}/ooxml`,
    {
      ooxml: params.ooxml,
      ooxml_hash: params.ooxmlHash,
      expected_section_version: params.expectedSectionVersion,
    },
  );
}

/** One item in a bulk Save. ``kind: "text"`` persists markdown (+ optional
 *  formatting snapshot); ``kind: "ooxml"`` is a formatting-only, version-checked
 *  upsert (text unchanged). */
export interface BulkEditItem {
  moduleId: string;
  kind: "text" | "ooxml";
  content?: string;
  ooxml?: string;
  ooxmlHash?: string;
  /** Required for ``kind: "ooxml"`` — the version the snapshot was captured at. */
  expectedSectionVersion?: number;
  /** For ``kind: "text"`` — the version this body was read at.
   *
   *  A different field from ``expectedSectionVersion`` because the server reads
   *  a different key for each kind, and they are not interchangeable: the ooxml
   *  path guards a formatting snapshot, this guards prose.
   *
   *  Omitted means **last-write-wins**, which is what every add-in save did
   *  until now: it re-read the document immediately beforehand and then sent no
   *  guard at all, so a web client's edit in between was silently overwritten
   *  with no conflict raised anywhere. */
  expectedVersion?: number;
}

/** Per-item outcome from the bulk-edit endpoint. */
export interface BulkEditResult {
  module_id: string | null;
  status: "applied" | "skipped" | "conflict";
  section_version?: number;
  current_version?: number;
  current_content?: string | null;
  reason?: string;
  ooxml_hash?: string;
}

/** Apply many section saves in ONE request (the Save button's whole-document
 *  sweep). Collapses N per-section ``/edit`` + ``/ooxml`` round-trips into one;
 *  the server applies each via a positional update (no full-doc rewrite) and
 *  returns per-item results so a stale section 409s in place. */
export async function bulkSubmitEdits(params: {
  runId: string;
  source?: "user" | "agent_redraft";
  edits: BulkEditItem[];
}): Promise<{ results: BulkEditResult[]; applied: number; total: number }> {
  return request("POST", `/runs/${encodeURIComponent(params.runId)}/sections/bulk-edit`, {
    source: params.source ?? "user",
    edits: params.edits.map((e) => ({
      module_id: e.moduleId,
      kind: e.kind,
      ...(e.content !== undefined ? { content: e.content } : {}),
      ...(e.ooxml && e.ooxmlHash ? { ooxml: e.ooxml, ooxml_hash: e.ooxmlHash } : {}),
      ...(e.expectedSectionVersion !== undefined ? { expected_section_version: e.expectedSectionVersion } : {}),
      ...(e.expectedVersion !== undefined ? { expected_version: e.expectedVersion } : {}),
    })),
  });
}

/** Persist writer edits to a title-page module's ``title_fields`` (label→value).
 *  The title page renders from ``title_fields``, not ``draft_full``, so the Save
 *  sweep captures it separately (``readTitlePageFields``) and posts changes here.
 *  Merges server-side without advancing lifecycle. */
export async function updateTitleFields(params: {
  runId: string;
  moduleId: string;
  titleFields: Record<string, string>;
  expectedVersion?: number;
}): Promise<{ module_id: string; section_version: number; at: string }> {
  return request(
    "POST",
    `/runs/${encodeURIComponent(params.runId)}/sections/${encodeURIComponent(params.moduleId)}/title-fields`,
    {
      title_fields: params.titleFields,
      ...(params.expectedVersion !== undefined ? { expected_version: params.expectedVersion } : {}),
    },
  );
}

/** One manifest row per module: current section_version joined against the
 *  stored snapshot's version + hash (no blobs). */
export interface SectionOoxmlManifestEntry {
  module_id: string;
  section_number: string;
  section_version: number;
  ooxml_version: number | null;
  ooxml_hash: string | null;
  content_length: number | null;
}

/** Fetch the OOXML snapshot manifest for the Save dirty-check. Returns
 *  ``null`` when the feature is off server-side (404) or the server predates
 *  it — callers degrade to markdown-only saves. */
export async function getSectionOoxmlManifest(
  runId: string,
): Promise<{ run_id: string; sections: SectionOoxmlManifestEntry[] } | null> {
  try {
    return await request("GET", `/runs/${encodeURIComponent(runId)}/sections/ooxml/manifest`);
  } catch {
    return null;
  }
}

/** Advance a section's lifecycle to ``verified`` or ``inserted``.
 *
 *  Use ``markSectionVerified`` after the user / QA pass approves a
 *  drafted section, and ``markSectionInserted`` after the Word write
 *  queue successfully inserts the section into the live document.
 *  Both are idempotent in practice — calling twice with the same
 *  state value is a no-op (the state machine rejects illegal jumps
 *  but the target state is the current state's only legal next on
 *  the happy path).
 */
async function transitionSection(
  runId: string,
  moduleId: string,
  to: "verified" | "inserted",
): Promise<{
  module_id: string;
  from: SectionLifecycleStatus;
  to: SectionLifecycleStatus;
  section_version: number;
  at: string;
}> {
  return request("POST", `/runs/${encodeURIComponent(runId)}/sections/${encodeURIComponent(moduleId)}/transition`, {
    to,
  });
}

export async function markSectionVerified(runId: string, moduleId: string) {
  return transitionSection(runId, moduleId, "verified");
}

export async function markSectionInserted(runId: string, moduleId: string) {
  return transitionSection(runId, moduleId, "inserted");
}

/** Build a URL the user can hit to open this run's docx in a new
 *  Word window. Embeds the session token so the freshly-opened window
 *  authenticates without a cookie. */
export function runDocumentUrl(runId: string, includeProvenance = true): string {
  const token = getSessionToken();
  const parts: string[] = [];
  if (token) parts.push(`token=${encodeURIComponent(token)}`);
  if (!includeProvenance) parts.push("include_provenance=false");
  const qp = parts.length > 0 ? `?${parts.join("&")}` : "";
  return `${API_BASE}/runs/${encodeURIComponent(runId)}/document${qp}`;
}

// ---------------------------------------------------------------------------
// Subscriptions / Quota
// ---------------------------------------------------------------------------

export async function getMyQuota(): Promise<{
  username: string;
  tier: string;
  status: string;
  plan_name: string | null;
  input_tokens_used: number;
  output_tokens_used: number;
  input_tokens_limit: number | null;
  output_tokens_limit: number | null;
  billing_period_start: string;
  billing_period_end: string;
}> {
  return request("GET", "/subscriptions/me");
}

// ---------------------------------------------------------------------------
// Billing / payments
//
// Native checkout: the add-in opens the Dodo checkout in the system browser and
// polls the intent until it settles, then starts the paid draft. Prices SHOWN to
// the writer come from ``/doc-types`` (live ``doc_pricing.json``); the charge is
// computed server-side. Nothing is gated — this is a purchase flow, and the
// add-in shows the order only when the active workspace is billable.
// ---------------------------------------------------------------------------

export type PaymentState = "created" | "completed" | "failed" | "expired" | "refunded";

/** One thing being bought: a whole document, one section of one, or a custom unlock. */
export interface CheckoutItem {
  doc_id: string;
  doc_type: string;
  /**
   * ``document`` buys a full draft, ``section`` one module of it, and ``custom``
   * the flat "build on your own document" unlock — a fixed fee priced off the
   * doctype grid that covers the whole imported doc.
   */
  scope: "document" | "section" | "custom";
  /** Top-level section number (e.g. "9") for scope "section" — not a module id. */
  module_id?: string;
}

/** What to draft once the payment clears. No ``doc_type``/``doc_id`` — the server derives both from the cart. */
export interface CheckoutRun {
  project_name: string;
  /** The writer-chosen draft name, replayed onto the run the payment starts. */
  name?: string;
  library_ids: string[];
  library_names: Record<string, string>;
  module_ids?: string[] | null;
  library_inventories?: Record<string, unknown>;
  insertion_mode_override?: "batch" | "section";
  /** The writer's final comments from the section picker, replayed onto the paid run. */
  run_instructions?: string;
  leaf_document_id?: string | null;
  leaf_parent_id?: string | null;
  workspace_id?: string | null;
  continue_run_id?: string | null;
  /** Where the submission is filed (a ``regions`` key from GET /doc-types). */
  region?: string;
}

export interface CheckoutHandoff {
  ref: string;
  checkout_url: string;
  amount_minor: number;
  currency: string;
  expires_at: string;
  provider: string;
  mode: string;
  /** Present only under the fake provider: event bodies to drive the loop by hand. */
  fake?: Record<string, unknown>;
}

/** One line of a receipt: a thing bought, at the price it was bought for. */
export interface ReceiptLine {
  /** The claim ref — "doc" for a whole document, "doc#9" for one section. */
  ref: string;
  doc_type: string;
  tier: string;
  scope: "document" | "section";
  label: string;
  price_minor: number;
}

export interface PaymentIntent {
  ref: string;
  state: PaymentState;
  doc_ids: string[];
  doc_types: string[];
  amount_minor: number;
  currency: string;
  charged_amount_minor: number | null;
  charged_currency: string | null;
  checkout_url: string | null;
  expires_at: string;
  created_at: string;
  paid_at: string | null;
  refunded_at: string | null;
  refunded_amount_minor: number | null;
  fulfilled_at: string | null;
  failure_reason: string | null;
  run_doc_id: string | null;
  run_continues: boolean;
  drafted: boolean | null;
  drafted_run_id: string | null;
  lines: ReceiptLine[];
  charged_tax_minor: number | null;
  provider_payment_id: string | null;
  card_version: string | null;
  provider_receipt_url: string | null;
  mode: string;
  provider: string;
}

export interface PaidRun {
  run_id: string;
  intent_ref: string;
  amount_minor: number;
  charged_amount_minor: number | null;
  charged_currency: string | null;
}

export interface DocumentPayment {
  doc_id: string;
  paid: boolean;
  mine: boolean;
  whole_document?: boolean;
  sections_paid?: string[];
  amount_minor?: number | null;
  charged_amount_minor?: number | null;
  charged_currency?: string | null;
  sections_covered?: string[];
  covered_whole?: boolean;
  intent_refs?: string[];
  paid_at?: string | null;
  runs?: PaidRun[];
  billing_required?: boolean;
  billing_reason?: string;
}

export interface PaymentsPage {
  items: PaymentIntent[];
  count: number;
  total: number;
}

export interface StartedPaidDraft {
  run_id: string;
  doc_id: string;
  doc_type: string;
  /** True when the document already had runs and this returned the existing one. */
  already_started: boolean;
}

export interface PaymentsQuery {
  limit?: number;
  offset?: number;
  state?: PaymentState;
}

/** Whether this workspace is sold to at all (the "demo = free, custom = paid" gate). */
export interface WorkspaceBilling {
  billing_required: boolean;
  billing_reason: string;
}

/**
 * Whether the active workspace should be shown prices at all.
 *
 * Reads ONLY ``billing_required``/``billing_reason`` off the rate card — the
 * rate card's own price numbers are deliberately ignored here; prices come from
 * ``/doc-types`` (live ``doc_pricing.json``). Fails open to "not required" so a
 * lookup blip never fronts a paywall on a free run.
 */
export async function getWorkspaceBilling(workspaceId: string): Promise<WorkspaceBilling> {
  try {
    const card = await request<{ billing_required?: boolean; billing_reason?: string }>(
      "GET",
      `/billing/rate-card?workspace_id=${encodeURIComponent(workspaceId)}`,
    );
    return {
      billing_required: card.billing_required === true,
      billing_reason: card.billing_reason ?? "unresolved",
    };
  } catch {
    return { billing_required: false, billing_reason: "unresolved" };
  }
}

/**
 * The signed-in address, when it is one. Pre-fills the provider's form only.
 */
export function checkoutEmail(username: string | null | undefined): string | undefined {
  if (!username) return undefined;
  const trimmed = username.trim();
  if (!trimmed.includes("@")) return undefined;
  return trimmed;
}

/** Open a checkout for a cart, or be told why not (409 payment_pending / already_paid, 422, 503). */
export async function createCheckout(body: {
  items: CheckoutItem[];
  run?: CheckoutRun;
  email?: string;
}): Promise<CheckoutHandoff> {
  return request("POST", "/billing/checkout", body);
}

/** Ask the PROVIDER where a payment got to (not our own record — a task-pane has no webhook URL). */
export async function refreshIntent(ref: string): Promise<PaymentIntent> {
  return request("POST", `/billing/intents/${encodeURIComponent(ref)}/refresh`);
}

/** Start the draft a completed payment bought. Idempotent on the document. */
export async function startPaidDraft(ref: string): Promise<StartedPaidDraft> {
  return request("POST", `/billing/intents/${encodeURIComponent(ref)}/draft`);
}

/** A page of this account's payments, newest first. */
export async function listIntents(query: PaymentsQuery = {}): Promise<PaymentsPage> {
  const params = new URLSearchParams({
    limit: String(query.limit ?? 50),
    offset: String(query.offset ?? 0),
  });
  if (query.state !== undefined) params.set("state", query.state);
  return request("GET", `/billing/intents?${params.toString()}`);
}

/** One payment, by ref — reads what we already know (for a receipt), no provider round-trip. */
export async function getIntent(ref: string): Promise<PaymentIntent> {
  return request("GET", `/billing/intents/${encodeURIComponent(ref)}`);
}

/** What was paid for one document. 404 for a document the caller cannot see. */
export async function getDocumentPayment(docId: string): Promise<DocumentPayment> {
  return request("GET", `/billing/documents/${encodeURIComponent(docId)}/payment`);
}

/** A single-use, short-lived link to the provider's own payment-history page. 503 when none. */
export async function createPortalSession(): Promise<{ url: string }> {
  return request("POST", "/billing/portal");
}

/** What a promo redemption asks for and unlocks. */
export interface PromoRequest {
  code: string;
  doc_id: string;
  doc_type: string;
  /** Mirrors ``CheckoutItem.scope`` — what the waiver claims. */
  scope: "document" | "section" | "custom";
  /** Level-1 root section numbers ("9") to unlock, for scope "section". */
  sections?: string[];
}

export interface PromoResult {
  /** False for an unknown code — a plain answer for the input field, not an error. */
  valid: boolean;
  /** True when the refs were already covered (a prior payment or redemption). */
  already_covered?: boolean;
  /** The claim refs now covered — "doc", or "doc#9" per section. */
  refs?: string[];
}

/**
 * Redeem a promo code. A valid one records a server-side grant so the run-start
 * gate passes; an unknown one returns ``{valid: false}`` without granting.
 */
export async function postPromo(body: PromoRequest): Promise<PromoResult> {
  return request("POST", "/promo", body);
}

// ---------------------------------------------------------------------------
// Checkpoints
// ---------------------------------------------------------------------------

export async function getCheckpoints(projectName?: string): Promise<CheckpointInfo[]> {
  const query = projectName ? `?project_name=${encodeURIComponent(projectName)}` : "";
  return request("GET", `/checkpoints${query}`);
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

export async function getSectionContext(sdtId: string): Promise<import("../types").SectionContext> {
  return request("GET", `/sections/${sdtId}/context`);
}

export async function chatAboutSection(
  sdtId: string,
  message: string,
): Promise<{ revised_content: string; message: string }> {
  return request("POST", `/sections/${sdtId}/chat`, { message });
}

// ---------------------------------------------------------------------------
// Document Chat
// ---------------------------------------------------------------------------

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatToolCall {
  tool: string;
  input: Record<string, unknown>;
  result_preview: string;
}

export interface WroteSection {
  section_number: string;
  content: string;
}

export interface ChatResponse {
  message: string;
  tool_calls: ChatToolCall[];
  wrote_sections: WroteSection[];
  conversation_id: string;
  token_usage: Record<string, number>;
}

/**
 * Streaming chat — uses the SSE endpoint for live tool call traces.
 *
 * Calls onEvent for each event as it arrives:
 *   {type: "tool_call_start", tool, input}
 *   {type: "tool_call_result", tool, result_preview}
 *   {type: "text", text}
 *   {type: "complete", message, tool_calls, wrote_sections, conversation_id, token_usage}
 *   {type: "error", error}
 */

// ---------------------------------------------------------------------------
// Conversation persistence
// ---------------------------------------------------------------------------

export interface ConversationSummary {
  conversation_id: string;
  created_at: number;
  last_activity: number;
  section_number: string | null;
  message_count: number;
  preview: string;
}

export interface ConversationDetail {
  conversation_id: string;
  section_number: string | null;
  created_at: number;
  last_activity: number;
  display_messages: Array<{
    role: "user" | "assistant";
    content: string;
    timestamp: number;
    tool_calls?: Array<{ tool: string; result_preview: string }>;
  }>;
  message_count: number;
}

/** List recent conversations (newest first). */
export async function listConversations(limit = 20): Promise<ConversationSummary[]> {
  return request("GET", `/conversations?limit=${limit}`);
}

/** Load a conversation's display messages for restoring chat history. */
export async function getConversation(conversationId: string): Promise<ConversationDetail> {
  return request("GET", `/conversations/${encodeURIComponent(conversationId)}`);
}

export async function chatWithDocumentStream(
  params: {
    message: string;
    docx_path: string;
    section_number?: string;
    conversation_id?: string;
    connection_id?: string;
    library_id?: string;
    trial?: string; // Current run's trial (scopes provenance)
    session?: string; // Current run's session (scopes provenance)
    /** DraftRunDoc id — lets the backend attribute marker removals from
     *  chat edits to the section-issue ledger (resolved-by-asthra). */
    run_id?: string;
    doc_type?: string; // CER, CSR, etc.
    /** Text the writer had highlighted in Word when they sent the message.
     *  Forwarded to the server so the agent sees it as explicit context
     *  rather than having to ask the writer to paste it. */
    selected_text?: string;
    /** Snapshot of the message the writer is replying to. Forwarded to
     *  the server so the agent sees the quoted context explicitly. */
    quoted_message?: Record<string, unknown>;
    /** Classified intent this message was routed under. Display/audit
     *  only server-side — it lands on the persisted user display
     *  message so restored history keeps the intent chip. */
    intent?: ChatRoutedIntent;
    /** "research" — chat "research highlight" intent: the backend
     *  prioritizes search tools, answers with citations, and drops
     *  document-write tools for the turn. Legacy endpoint only. */
    mode?: "default" | "research";
    /** When set, the request hits the v3 task-orchestrated agent
     *  (POST /api/chat-stream/v3) which emits the additional SSE
     *  events (plan_ready, task_*, retrieval_step, drafting_response).
     *  Absent → legacy /api/chat-stream endpoint (vanilla tool loop). */
    use_agent_v3?: boolean;
    /** Optional library-mode override for the v3 cascade. Ignored
     *  when ``use_agent_v3`` is false. */
    library_mode?: "single_doc" | "multi_doc_sequential" | "full_library";
    /** Writer-attached source assets (@-mentions) — resolved server-side
     *  before the tool loop and injected as ready-to-use context. */
    attachments?: MentionAttachment[];
  },
  onEvent: (event: Record<string, unknown>) => void,
  /** When the writer clicks Stop in the composer, the caller aborts
   *  this signal — the fetch below rejects with an ``AbortError`` and
   *  the read loop unwinds, dropping the connection so the backend
   *  ``StreamingResponse`` cancels its generator. */
  signal?: AbortSignal,
): Promise<void> {
  const chatHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const chatToken = getSessionToken();
  if (chatToken) {
    chatHeaders.Authorization = `Bearer ${chatToken}`;
  }

  // Strip client-only fields so the backend doesn't see unknown keys.
  const { use_agent_v3, library_mode, attachments, ...wireParams } = params;
  const wireAttachments = (attachments ?? []).map(toWireAttachment);
  const path = use_agent_v3 ? "/chat-stream/v3" : "/chat-stream";
  const body = use_agent_v3
    ? { ...wireParams, library_mode, attachments: wireAttachments }
    : { ...wireParams, attachments: wireAttachments };

  const resp = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: chatHeaders,
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    // Parse SSE events: each event is "data: <json>\n\n"
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (line.startsWith("data: ")) {
        try {
          const event = JSON.parse(line.slice(6));
          onEvent(event);
        } catch {
          // Skip malformed events
        }
      }
    }
  }
}

/**
 * Stream the `/abbreviations` command's build. GETs the SSE route and
 * invokes ``onEvent`` per parsed event. Emits the same event vocabulary
 * as the chat stream (``tool_call_start`` / ``tool_call_result`` /
 * ``complete`` / ``error``) so the caller can feed it straight into the
 * chat bridge's ``handleStreamEvent`` — the live tool-step UI renders for
 * free, and the ``complete`` message carries the finished markdown table.
 *
 * Abortable via ``AbortSignal`` (writer clicks Stop).
 */
export async function streamAbbreviations(
  runId: string,
  onEvent: (event: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(`${API_BASE}/runs/${encodeURIComponent(runId)}/abbreviations/stream`, {
    method: "GET",
    credentials: "include",
    headers,
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    // Parse SSE events: each event is "data: <json>\n\n"
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (line.startsWith("data: ")) {
        try {
          const event = JSON.parse(line.slice(6));
          onEvent(event);
        } catch {
          // Skip malformed events
        }
      }
    }
  }
}

export async function chatWithDocument(params: {
  message: string;
  docx_path: string;
  section_number?: string;
  conversation_id?: string;
  connection_id?: string;
  library_id?: string;
}): Promise<ChatResponse> {
  return request("POST", "/chat", params);
}

// ---------------------------------------------------------------------------
// Document Session (UI state persistence, keyed by AsthrDocumentId)
// ---------------------------------------------------------------------------

export interface DocumentSession {
  doc_id: string;
  /** Which eCTD leaf this document IS, and which submission's leaf set it
   *  belongs to. Both null for a doctype filed as ONE document (CSR,
   *  PROTOCOL_DRUG, PSUR and most others), and for every document created
   *  before the leaf split — `scopeCatalogToLeaf` widens back to the whole
   *  doctype for a null id, so those documents render exactly as they did. */
  leaf_document_id: string | null;
  leaf_parent_id: string | null;
  workspace_id: string | null;
  library_id: string | null;
  library_name: string | null;
  doc_code: string | null;
  doc_name: string | null;
  doc_framework: string | null;
  backend_doc_type: string | null;
  trial: string | null;
  session_id: string | null;
  run_id: string | null;
  dismissed_run_id: string | null;
  insertion_mode: "batch" | "section" | null;
  plan_id: string | null;
  conversation_id: string | null;
  current_approval_id: string | null;
  // Cached library resolution from the last successful resolve/approve
  // on this session. Hydrated by useSessionBootstrap so LibraryHub and
  // useWorkspaceLibrary can skip the SSE re-stream when (library_id,
  // library_resolved_doctype, library_snapshot_hash) all match.
  library_inventory_snapshot: ResolvedInventoryDTO | null;
  library_snapshot_hash: string | null;
  library_resolved_doctype: string | null;
  section_commit: Record<string, unknown> | null;
  spine_snapshot: DocumentSpineSnapshotSection[] | null;
  timeline_filter: Record<string, unknown> | null;
  focused_section_id: string | null;
  ui_phase: string;
  view_mode: string;
  updated_at: string;
}

export interface DocumentSpineSnapshotSection {
  id: string;
  number: string;
  title: string;
  status: string;
  subline?: string | null;
  sublineStartedAt?: number | null;
  citationCount?: number | null;
  retrievalProgress?: {
    resolved: number;
    total: number;
    writtenOff: number;
  } | null;
  requirementCount?: number | null;
  wordCount?: number | null;
  quality?: string | null;
  flaggedCount?: number | null;
  contentGapCount?: number | null;
  regulatorFlagCount?: number | null;
  acceptedAt?: number | null;
  /** Custom-submission template match (Part D): the template module_id this
   *  custom heading was matched to (or null once resolved to "no match"). */
  matchedModuleId?: string | null;
  /** The template section title this heading matched — surfaced in the draft
   *  panel and persisted so the Haiku check never re-runs. */
  matchedTitle?: string | null;
  /** True once the match resolution ran (matched OR confirmed no-match), so
   *  regenerate / audit reuse the stored result instead of re-checking. */
  matchResolved?: boolean | null;
  /** Custom-submission per-section drafting instruction the writer authored. */
  instruction?: string | null;
  /** Drafting FAILED for this section (server ``module_error``) — restored so
   *  a reloaded spine keeps the failed dot instead of a review flag. */
  failed?: boolean | null;
  /** Why drafting failed, paired with ``failed``. */
  failureReason?: string | null;
}

/**
 * Partial document-session patch.
 *
 * Each field is a tri-state:
 *   - omitted (undefined): leave the stored value untouched
 *   - non-null value: overwrite the stored value
 *   - explicit ``null``: clear the field on the backend (translated by
 *     ``upsertDocumentSession`` into a ``clear_fields`` entry; the
 *     route processes it via Beanie ``Unset``).
 *
 * The ``null`` case exists because the backend strips null fields from
 * partial saves (``model_dump(exclude_none=True)``) — without an
 * explicit clear path, stale neighbours like ``plan_id`` from a prior
 * project survive across reused-document workflows.
 */
export interface DocumentSessionPatch {
  last_modified_by?: string;
  /** The writer's leaf choice, sent once at creation. Never persisted blindly:
   *  `server/leaf_identity.py` writes it only when the doctype declares that
   *  leaf, and declines rather than guesses otherwise — so a wrong id is
   *  dropped, not stored. Omit `leaf_parent_id` rather than sending null when
   *  no submission was named: the server seeds one, and a null would store an
   *  absent parent beside a present leaf. */
  leaf_document_id?: string;
  leaf_parent_id?: string;
  workspace_id?: string | null;
  library_id?: string | null;
  library_name?: string | null;
  doc_code?: string | null;
  doc_name?: string | null;
  doc_framework?: string | null;
  backend_doc_type?: string | null;
  trial?: string | null;
  session_id?: string | null;
  run_id?: string | null;
  dismissed_run_id?: string | null;
  insertion_mode?: "batch" | "section" | null;
  plan_id?: string | null;
  conversation_id?: string | null;
  current_approval_id?: string | null;
  library_inventory_snapshot?: ResolvedInventoryDTO | null;
  library_snapshot_hash?: string | null;
  library_resolved_doctype?: string | null;
  section_commit?: Record<string, unknown> | null;
  spine_snapshot?: DocumentSpineSnapshotSection[] | null;
  timeline_filter?: Record<string, unknown> | null;
  focused_section_id?: string | null;
  ui_phase?: string;
  view_mode?: string;
  clear_fields?: string[];
}

export interface DocumentActivity {
  doc_id: string;
  sequence: number;
  activity_type: string;
  actor: string;
  payload: Record<string, unknown>;
  session_id: string | null;
  run_id: string | null;
  plan_id: string | null;
  library_id: string | null;
  approval_id: string | null;
  conversation_id: string | null;
  created_by: string | null;
  timestamp: string;
}

export interface DocumentPlan {
  doc_id: string;
  plan_id: string;
  session_id: string | null;
  run_id: string | null;
  library_id: string | null;
  doc_type: string | null;
  created_by: string | null;
  status: string;
  plan_payload: Record<string, unknown>;
  plan_summary: unknown;
  api_healthy: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentHydrateResponse {
  doc_id: string;
  session: DocumentSession | null;
  messages: Record<string, unknown>[];
  activities: DocumentActivity[];
  plan: DocumentPlan | null;
  restored_at: string;
}

/** Fetch the persisted session for a document. Returns null on 404 (cold start). */
export async function getDocumentSession(docId: string): Promise<DocumentSession | null> {
  try {
    return await request<DocumentSession>("GET", `/document-sessions/${docId}`);
  } catch (err) {
    if (err instanceof Error && err.message.includes("404")) return null;
    throw err;
  }
}

/** Upsert the session for a document with a partial patch.
 *
 * Translates ``null`` field values into a backend-side ``clear_fields``
 * directive before sending — see the ``DocumentSessionPatch`` doc
 * comment for why this indirection exists.
 */
export async function upsertDocumentSession(docId: string, patch: DocumentSessionPatch): Promise<void> {
  const setPart: Record<string, unknown> = {};
  const clearFields: string[] = [];
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) {
      clearFields.push(key);
    } else if (value !== undefined) {
      setPart[key] = value;
    }
  }
  const body = clearFields.length === 0 ? setPart : { ...setPart, clear_fields: clearFields };
  await request<DocumentSession>("PUT", `/document-sessions/${docId}`, body);
}

/** Fetch the authoritative hydrate payload for a document. */
export async function getDocumentHydrate(docId: string): Promise<DocumentHydrateResponse> {
  return request<DocumentHydrateResponse>("GET", `/document-sessions/${docId}/hydrate`);
}

/** One workspace note the drafting agents keep across sections. */
export interface WorkspaceNote {
  key: string;
  content: string;
  version: number;
  updated_at: string | null;
  updated_by_run: string;
}

export interface DocumentNotesResponse {
  /** "folder" — shared by every document filed in the folder; "document" — this document only. */
  scope: { kind: "folder" | "document" | "run"; scope_id: string; label: string };
  library_id: string | null;
  notes: WorkspaceNote[];
}

/** The drafting agents' workspace notes for a document (its folder's when filed). Read-only. */
export async function getDocumentNotes(docId: string, signal?: AbortSignal): Promise<DocumentNotesResponse> {
  return request<DocumentNotesResponse>("GET", `/document-sessions/${docId}/notes`, undefined, signal);
}

// ---------------------------------------------------------------------------
// Document Chat (variant-c timeline history, keyed by AsthrDocumentId)
// ---------------------------------------------------------------------------

/** Backward-pagination options for the chat history endpoints. */
export interface ChatPageOptions {
  /** Newest N messages (server caps at 1000). Omit for the full array. */
  limit?: number;
  /** ``_id`` of the oldest already-loaded message — pages backward from it. */
  before?: string;
  /** ``at`` epoch-ms of that message; server fallback when the ``_id`` is gone. */
  beforeAt?: number;
}

/** One page of persisted chat history. */
export interface ChatPage {
  messages: Record<string, unknown>[];
  /** True when older messages exist before this page. */
  hasMore: boolean;
  /** Scope-filtered total on the server, independent of the page window. */
  total?: number;
}

function chatQuery(opts?: ChatPageOptions): string {
  if (!opts) return "";
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.before) qs.set("before", opts.before);
  if (opts.beforeAt !== undefined) qs.set("before_at", String(opts.beforeAt));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

interface ChatEnvelopeDTO {
  messages: Record<string, unknown>[];
  has_more?: boolean;
  total?: number;
}

/**
 * Fetch persisted chat messages for a document.
 * Returns null on 404 (no history yet — frontend uses localStorage).
 * Typed as unknown[] so callers can cast to their concrete ChatMessage type.
 */
export async function getDocumentChat(docId: string, opts?: ChatPageOptions): Promise<ChatPage | null> {
  try {
    const r = await request<ChatEnvelopeDTO>("GET", `/document-sessions/${docId}/chat${chatQuery(opts)}`);
    return {
      messages: r.messages,
      hasMore: r.has_more ?? false,
      total: r.total,
    };
  } catch (err) {
    // ``request`` attaches the HTTP status; matching on it (rather
    // than the message text, which is the server's detail string)
    // makes the null-on-404 contract actually hold.
    if (err instanceof Error && (err as { status?: number }).status === 404) return null;
    throw err;
  }
}

/**
 * Append new (delta) messages to the persisted chat history for a document.
 * Sends only messages not yet acknowledged by the server; the server dedupes
 * on ``_id`` so retries are safe.
 */
export async function appendDocumentChatMessages(
  docId: string,
  messages: Record<string, unknown>[],
  runId: string | null = null,
): Promise<void> {
  await request("POST", `/document-sessions/${docId}/chat/messages`, {
    messages,
    run_id: runId,
  });
}

/**
 * Replace the full chat message array for a document.
 * Kept as a safety-net for the unload keepalive flush path.
 * Normal appends now use ``appendDocumentChatMessages``.
 */
export async function syncDocumentChat(
  docId: string,
  messages: Record<string, unknown>[],
  runId: string | null = null,
): Promise<void> {
  await request("PUT", `/document-sessions/${docId}/chat`, {
    messages,
    run_id: runId,
  });
}

/**
 * Fetch chat messages scoped to a specific run.
 * The server returns messages tagged with this ``runId`` plus session-level
 * messages (``run_id == null``) so loading an older run hydrates the chat
 * to what was said while that run was active.
 *
 * Returns null on network failure / 404 so callers can fall back to the
 * doc-level chat or empty seed.
 */
export async function getRunChat(runId: string, opts?: ChatPageOptions): Promise<ChatPage | null> {
  try {
    const r = await request<ChatEnvelopeDTO>("GET", `/runs/${runId}/chat${chatQuery(opts)}`);
    return {
      messages: r.messages,
      hasMore: r.has_more ?? false,
      total: r.total,
    };
  } catch (err) {
    // See getDocumentChat — status-based 404 detection.
    if (err instanceof Error && (err as { status?: number }).status === 404) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// v2 Spine + Timeline — Phase 4 endpoints (session_v2 schema)
// ---------------------------------------------------------------------------

/** One section row returned by ``GET /api/sessions/{docId}/spine``. */
export interface SpineItemDTO {
  id: string;
  number: string;
  title: string;
  /** Matches SpineSectionStatus: queued | drafting | drafted | needs_approval | flagged */
  status: string;
  word_count: number | null;
  citation_count: number | null;
  /** ISO-8601 datetime string; epoch-ms conversion happens in the mapping helper. */
  accepted_at: string | null;
  quality: "high" | "medium" | "low" | null;
  flagged_count: number | null;
  content_flag_counts?: Record<string, number> | null;
  content_gap_count?: number | null;
  /** Count of [REGULATOR FLAG: …] recommendations persisted by the audit —
   *  drives the spine regulator badge on reopen (live path uses SSE). */
  regulator_flag_count?: number | null;
  regulatory_audited_at?: string | null;
  requirement_count: number | null;
  retrieval_progress: {
    resolved: number;
    total: number;
    writtenOff: number;
  } | null;
  /** Persisted per-section token telemetry + drafting model backend. 0/None on
   *  older runs or sections that have not drafted yet. */
  input_tokens?: number | null;
  output_tokens?: number | null;
  draft_model?: string | null;
  /** Custom-submission template match (existing-doc runs). Resolved lazily on
   *  the first regenerate / audit and persisted so it never re-runs. */
  matched_module_id?: string | null;
  matched_title?: string | null;
  match_resolved?: boolean | null;
  /** Custom-submission per-section drafting instruction (existing-doc runs).
   *  Authored by the writer; gates whether the section can be drafted. */
  instruction?: string | null;
  /** Custom-submission title-page marker (existing-doc runs). True for the
   *  synthetic leading "Title Page" row; the Load path restores it as leading
   *  chrome instead of scaffolding a numbered heading. */
  is_title?: boolean | null;
  /** Drafting failed for this module (paired with ``status: "flagged"``) —
   *  no body was produced. Distinguishes a failure from a review flag. */
  module_error?: boolean | null;
  /** Why drafting failed, when ``module_error`` is set. */
  error?: string | null;
}

export interface SpineDTO {
  doc_id: string;
  run_id: string | null;
  run_status: string | null;
  /** "custom" when built on an existing Word doc; null/absent for a fresh draft. */
  submission_mode?: string | null;
  spine: SpineItemDTO[];
}

/**
 * Fetch the full spine for a document from the v2 DB schema.
 *
 * Returns a ``SpineDTO`` with real ``wordCount``, ``citationCount``,
 * ``acceptedAt``, ``quality``, and ``flaggedCount`` values — none of
 * which are derivable from the legacy ``RunStatus.completed_module_ids``
 * list.
 *
 * Throws on any non-2xx response (caller should fall back to
 * ``hydrateSpineSections`` for old runs that predate the v2 schema).
 */
export async function fetchSpine(docId: string): Promise<SpineDTO> {
  return request("GET", `/sessions/${encodeURIComponent(docId)}/spine`);
}

/** One event row returned by ``GET /api/runs/{runId}/timeline``. */
export interface TimelineEventDTO {
  ts: number;
  actor: string;
  section_id: string | null;
  section_label: string | null;
  run_id: string | null;
  payload: Record<string, unknown>;
}

export interface TimelineDTO {
  run_id: string;
  events: TimelineEventDTO[];
}

/** Canonical run snapshot used for deterministic refresh hydration.
 * Extends RunStatus with spine rows, timeline events, and snapshot metadata.
 * Defined as a type alias (not interface) because TypeScript does not allow
 * `import()` expressions in an `extends` clause.
 */
export type RunSnapshotDTO = import("../types").RunStatus & {
  doc_id: string;
  /** The run's (trial, session) pair — the identity the citations lookup
   *  keys on. A loaded run never replays ``run_metadata``, so Load reads it
   *  from here. Null on runs whose runner never started. */
  trial?: string | null;
  session_id?: string | null;
  /** "custom" when this run was built on an existing Word document — the spine
   *  is the document's own heading outline. None/absent for a fresh draft. */
  submission_mode?: string | null;
  /** Where the submission is filed (``SUBMISSION_REGIONS`` code), persisted on
   *  the run at start. Null only on runs that predate regions. */
  region?: string | null;
  spine: SpineItemDTO[];
  events: TimelineEventDTO[];
  snapshot_ts: number;
  snapshot_version: string;
  source_hint: string;
  /** Per-module Phase 3 streaming text for modules currently being drafted.
   *  Populated only for non-terminal runs; empty otherwise.  Lets the
   *  rejoin path render the partial draft for the active module
   *  immediately instead of waiting for the next ``section_ready`` SSE.
   *  Keys are module ids; values are the running raw text accumulated
   *  by the backend text-chunk callback (pre-sanitization). */
  streaming_drafts?: Record<string, string>;
  /** Failure reason of a ``failed`` run. */
  error?: string | null;
  /** Set when a failed-but-resumable run is presented as paused (see
   *  {@link presentResumableFailure}): the notice shown beside Resume. */
  failure_notice?: string;
};

/**
 * A run that failed (crash, out of memory, worker restart, provider error) but
 * can pick up from where it stopped is offered to the writer exactly like a
 * paused run: every restore / Load / pause-settle path already knows how to
 * show Resume for ``paused``, and the server's resume accepts failed runs with
 * ``resumable``. A killed (cancelled) run is never resumable.
 */
export function presentResumableFailure(snap: RunSnapshotDTO): RunSnapshotDTO {
  if (snap.status !== "failed" || !snap.resumable) return snap;
  const reason = (snap.error ?? "").replace(/^Pipeline failed:\s*/, "").trim();
  return {
    ...snap,
    status: "paused",
    failure_notice: reason
      ? `Drafting stopped because of an error (${reason.slice(0, 200)}). Resume continues from where it stopped.`
      : "Drafting stopped because of an error. Resume continues from where it stopped.",
  };
}

/**
 * Fetch the real-timestamp event timeline for a run.
 *
 * Returns events ordered chronologically with wall-clock timestamps
 * from ``DraftRunDoc.gates[].modules[].completed_at`` — no timestamp
 * synthesis required.
 *
 * Throws on any non-2xx response (caller should fall back to
 * ``synthesiseDraftEvents`` for old runs).
 */
export async function fetchRunTimeline(runId: string): Promise<TimelineDTO> {
  return request("GET", `/runs/${encodeURIComponent(runId)}/timeline`);
}

/**
 * Fetch a canonical backend snapshot for one run.
 * This payload is the single status authority for refresh/pause/resume hydration.
 */
export async function getRunSnapshot(runId: string): Promise<RunSnapshotDTO> {
  const snap: RunSnapshotDTO = await request("GET", `/runs/${encodeURIComponent(runId)}/snapshot`);
  return presentResumableFailure(snap);
}

// ---------------------------------------------------------------------------
// Feature Flags
// ---------------------------------------------------------------------------

export interface EvaluationResult {
  key: string;
  enabled: boolean;
}

/**
 * Evaluate ALL feature flags for the currently authenticated user.
 * Returns each flag's key and whether it is active for the caller.
 */
export async function evaluateAllFlags(): Promise<EvaluationResult[]> {
  return request("GET", "/flags/evaluate");
}

// ---------------------------------------------------------------------------
// Source impact — which sections read a (since updated) source document
// ---------------------------------------------------------------------------

export type SourceImpactStrength = "high" | "medium" | "low";

export interface SourceImpactHit {
  transaction_id: string;
  tool: string;
  via: string | null;
  page_no: number | null;
  pages: Array<number | string>;
  query: string | null;
  classification: string | null;
  strength: SourceImpactStrength;
  excerpt: string | null;
}

export interface SourceImpactSection {
  module_id: string;
  section_number: string | null;
  title: string;
  strength: SourceImpactStrength;
  hit_count: number;
  tools: string[];
  hits: SourceImpactHit[];
}

export interface SourceImpactResponse {
  run_id: string;
  document: string;
  /** True when the run predates multi-document name capture: a section whose
   *  search used this document as a second hit may be missing from the list. */
  names_may_be_partial: boolean;
  sections: SourceImpactSection[];
}

/** Sections of ``runId`` whose current draft read the library document
 *  ``document`` (matched by name), strongest use first, with excerpts. */
export async function fetchSourceImpact(
  runId: string,
  document: string,
  signal?: AbortSignal,
): Promise<SourceImpactResponse> {
  const qs = new URLSearchParams({ document, excerpts: "true" });
  return request("GET", `/runs/${encodeURIComponent(runId)}/source-impact?${qs.toString()}`, undefined, signal);
}
